// Flow Instance Types (shared between web and api apps)

import type { RiskScoreCapState } from "../conditional-sources";
import type { LocalizedString } from "../i18n";
import type { SuitabilityIssueType, SuitabilityStatus } from "../suitability";
import type { AccountMinimumViolation } from "./account-minimum";
import type {
  AnsweringMode,
  AnsweringPartyEvent,
  AnsweringPartyRecord,
  FrozenSlotIdentity,
  MemberRepresentative,
} from "./answering-party";
import type { CapacityInputFrequency, CapacityMultiplierBasis } from "./financial-situation-models";
import type { FlowStatus } from "./flows";
import type { IdentityConflictResolution } from "./identity-verification";
import type { MinBuyViolation } from "./min-buy";
import type { PdfGenerationLog } from "./pdf-templates";
import type {
  AssessmentModeStatus,
  ProductAssessmentResultSource,
  ProductAssessmentVerdict,
} from "./product-assessment-verdict";
import type { ProductTypeAssessmentMode, QuizAttemptAvailability } from "./product-assessments";
import type { QuizPublic, QuizPublicQuestion, QuizResultStatus } from "./quizzes";
import type { RiskInputsIncompleteReason } from "./risk-models";
import type { SustainabilityCriterionDescriptor } from "./sustainability";

/** Response shape for per-goal snapshot GET endpoints. */
export type GoalSnapshotResponse<T = unknown> = {
  data: T | null;
  generatedAt: string | null;
};

export type SnapshotType =
  | "portfolio"
  // QL-707 — the custom builder rows AS ADVISED on a custom portfolio (every
  // row incl. 0-amount sells and keeps, plus the switch pairs with their ids),
  // frozen beside "portfolio" (which is the account AFTER execution). Written
  // for every custom portfolio and read by locked-instance portfolio GETs only;
  // internal: never listed by the snapshot listings, never in refreshHints.
  | "advisedPortfolio"
  | "xray"
  | "assetAllocation"
  | "historicalReturn"
  | "forecast"
  | "cost"
  | "expectedValue"
  // QP-179 — per-portfolio sustainability matching result. Generated only when
  // the flow's collection has a sustainabilityConfig and the instance's
  // sustainability preferences say important = yes.
  | "sustainability"
  // QP-224 — live client-preferences answers. Instance-global (never keyed by
  // goal/portfolio) and never emitted in SnapshotInvalidation refresh hints;
  // registered here so both apps share one snapshot-type registry.
  | "clientPreferences"
  // QL-315 — the asset-class forecast. One refresh type for both stored rows
  // (goal-scoped `asset_class_forecast` and instance-scoped
  // `instance_asset_class_forecast`): a portfolio edit moves both, so the
  // frontend never needs to invalidate one without the other.
  | "assetClassForecast"
  // QL-416 — per-portfolio cost/benefit comparison (existing vs new metrics +
  // one-time transition costs). Regenerated with the other portfolio snapshots.
  | "costBenefit"
  // QL-375 — the goal's mandate check (goal-scoped `mandate`), regenerated with
  // the asset-class forecast it reads its basis from.
  | "mandate";

/**
 * Returned by any mutation that touches a portfolio's snapshots (QP-150).
 * `invalidated` lists every snapshot type that may have changed — the frontend
 * should re-fetch those panels (immediately if visible, lazily if hidden).
 * `cleared` is set when snapshots were deleted rather than regenerated.
 * A mutation that affects several portfolios (e.g. a goal risk change) returns
 * one entry per portfolio (a `SnapshotInvalidation[]`).
 */
export type SnapshotInvalidation = {
  portfolioId: string;
  invalidated: SnapshotType[];
  cleared?: true;
};

export enum ScheduleType {
  ONE_TIME = "one_time",
  RECURRING = "recurring",
}

export enum RecurringFrequency {
  MONTHLY = "monthly",
  QUARTERLY = "quarterly",
  YEARLY = "yearly",
}

// --- Flow Instance Process ---

/**
 * Lifecycle status of a flow instance. Status IS the process step:
 * draft → in_progress → submitted ⟷ pdf_generated → compliance_review
 * → external_review → signing → completed. `draft` is the freshly-created
 * state; it flips to `in_progress` once any value is saved (the instance is
 * still being edited). Steps between pdf_generated and completed only apply
 * when enabled on the flow (process flags). `cancelled` is reachable from
 * the editable states only and never enters the process. `completed` is terminal.
 */
export const FLOW_INSTANCE_STATUSES = [
  "draft",
  "in_progress",
  "cancelled",
  "submitted",
  "pdf_generated",
  "compliance_review",
  "external_review",
  "signing",
  "completed",
] as const;

export type FlowInstanceStatus = (typeof FLOW_INSTANCE_STATUSES)[number];

/**
 * Statuses where the instance has been submitted (post-submission process or
 * terminal). Useful for "submitted or beyond" checks — e.g. which instances
 * count as a source for previous-answer reuse or dashboard stats.
 */
export const SUBMITTED_FLOW_INSTANCE_STATUSES = [
  "submitted",
  "pdf_generated",
  "compliance_review",
  "external_review",
  "signing",
  "completed",
] as const satisfies readonly FlowInstanceStatus[];

/**
 * QL-263 — statuses at which a STANDALONE-launched advisor is handed back to
 * the partner system (`internalRedirectUrl`). The review steps run in the
 * background and can take time (compliance decides in its own session, the
 * external system over the public API), and `completed` is terminal. The
 * partner reopens the instance through its launch URL when there is more to
 * do. `submitted` / `pdf_generated` and `signing` stay in QP: the advisor
 * still has to generate or confirm the PDF, or create the signature order,
 * from the process page. `cancelled` is deliberately NOT here: the wizard
 * returns a standalone advisor on cancel on its own (QL-737), and listing it
 * would also start the process-page countdown when another session cancels.
 */
export const STANDALONE_RETURN_STATUSES = [
  "compliance_review",
  "external_review",
  "completed",
] as const satisfies readonly FlowInstanceStatus[];

/** See {@link STANDALONE_RETURN_STATUSES}. */
export function isStandaloneReturnStatus(status: string): boolean {
  return (STANDALONE_RETURN_STATUSES as readonly string[]).includes(status);
}

/**
 * Editing field/component values is possible while the instance is still being
 * worked on — `draft` (freshly created) or `in_progress` (a value has been
 * saved but not yet submitted). Any submitted/process/terminal/cancelled state
 * is locked.
 */
export const EDITABLE_FLOW_INSTANCE_STATUSES = [
  "draft",
  "in_progress",
] as const satisfies readonly FlowInstanceStatus[];

/** See {@link EDITABLE_FLOW_INSTANCE_STATUSES}. */
export function isFlowInstanceEditable(status: string): boolean {
  return (EDITABLE_FLOW_INSTANCE_STATUSES as readonly string[]).includes(status);
}

/** True once an instance has been submitted (any process step or completed). */
export function isFlowInstanceSubmitted(status: string): boolean {
  return (SUBMITTED_FLOW_INSTANCE_STATUSES as readonly string[]).includes(status);
}

/**
 * Whether the "cancel advice" dialog asks for a cancellation reason.
 * `hidden` — no reason field (default); `optional` — field shown, may be left
 * empty; `required` — cancelling is blocked until a reason is entered
 * (enforced server-side).
 */
export const CANCEL_REASON_MODES = ["hidden", "optional", "required"] as const;

export type CancelReasonMode = (typeof CANCEL_REASON_MODES)[number];

export type ApprovalStepType = "compliance" | "external";

export type ApprovalStatus = "pending" | "approved" | "rejected" | "withdrawn";

export type FlowInstanceApproval = {
  id: string;
  flowInstanceId: string;
  stepType: ApprovalStepType;
  status: ApprovalStatus;
  comment: string | null;
  decidedById: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  createdAt: string;
};

/** One compliance approval (pending or decided) in the tenant-wide list. */
export type ComplianceQueueItem = {
  approvalId: string;
  status: ApprovalStatus;
  requestedAt: string;
  requestedByName: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
  comment: string | null;
  flowInstanceId: string;
  flowInstanceName: string | null;
  flowName: string;
  clientId: string;
  clientName: string;
};

/** Every kind of process action recorded in the activity log. */
export const FLOW_PROCESS_EVENT_TYPES = [
  // QL-452 — the first saved field value moved a draft to in_progress.
  "started",
  "submitted",
  "unlocked",
  "drafted",
  "cancelled",
  "reopened",
  "pdf_generated",
  "pdf_deleted",
  "compliance_requested",
  "compliance_approved",
  "compliance_rejected",
  "compliance_withdrawn",
  "external_requested",
  "external_update",
  "external_approved",
  "external_rejected",
  "external_withdrawn",
  "signature_requested",
  "signature_cancelled",
  "signed",
  "completed",
  "accounts_orders_created",
  // Flow setting "auto-generate PDF on submit": the PDF was rendered by the
  // system as part of submitting, or that attempt failed and the instance
  // stayed at `submitted` for the advisor to generate manually.
  "auto_pdf_generated",
  "auto_pdf_failed",
] as const;

export type FlowProcessEventType = (typeof FLOW_PROCESS_EVENT_TYPES)[number];

// --- Submit automation (flow.autoGeneratePdfOnSubmit) ---

/**
 * What the "auto-generate PDF on submit" flow setting did for one submit.
 *
 *   disabled     setting off — the advisor generates the PDF manually
 *   generated    PDF rendered and the process advanced past the PDF step
 *   no_template  setting on, but the flow has no active PDF template
 *   failed       generation failed; the instance remains at `submitted`
 *   skipped      nothing ran — the instance was already past submission
 */
export const SUBMIT_AUTOMATION_PDF_OUTCOMES = [
  "disabled",
  "generated",
  "no_template",
  "failed",
  "skipped",
] as const;

export type SubmitAutomationPdfOutcome = (typeof SUBMIT_AUTOMATION_PDF_OUTCOMES)[number];

/**
 * Result of submitting an instance, returned by every submit surface (QL-263).
 *
 * `status` is the process status once submit and any automation finished.
 * Generation runs synchronously inside the submit call, so it already reflects
 * the step the process advanced to — a human step, or `completed` — and is the
 * field callers use to decide whether the process is already over.
 */
export type SubmitInstanceResult = {
  status: FlowInstanceStatus;
  autoPdf: SubmitAutomationPdfOutcome;
  /** PDFs rendered by the automation (empty unless `autoPdf` is `generated`). */
  pdfs: PdfGenerationLog[];
  /** Why generation did not (fully) happen — also written to the activity log. */
  pdfErrors: string[];
};

/** Response of POST /internal/flow-instances/:id/complete. */
export type SubmitFlowInstanceResponse = SubmitInstanceResult & { success: true };

/** One entry in the process activity log. */
export type FlowProcessEvent = {
  id: string;
  type: FlowProcessEventType;
  comment: string | null;
  actorName: string | null;
  /** Status the instance left with this action (QL-292: "returned to draft from …"). */
  fromStatus: FlowInstanceStatus | null;
  createdAt: string;
};

/**
 * QL-292 — return an unfinished flow instance to draft from any process step.
 * `reason` (max 500) is recorded as the comment on the `drafted` activity event.
 */
export type DraftFlowInstanceRequest = {
  reason?: string | null;
};

/** What drafting removed on the way back — every count is 0 on a no-op. */
export type DraftFlowInstanceCleanup = {
  pdfsDeleted: number;
  signatureOrdersCancelled: number;
  externalReviewWithdrawn: boolean;
  complianceWithdrawn: boolean;
};

export type DraftFlowInstanceResponse = {
  id: string;
  status: "draft";
  /** Status before the call; equals `draft` / `in_progress` when nothing had to move. */
  previousStatus: FlowInstanceStatus;
  cleanup: DraftFlowInstanceCleanup;
};

// --- QL-399: flow-page process info (stage, feedback, reopening) ---

/**
 * Compliance review of ALREADY COMPLETED advice — written by
 * compliance-check-service, not by the in-process approval gate, and so
 * deliberately not part of `FLOW_PROCESS_EVENT_TYPES`. Listed here because
 * these remarks DO surface as feedback cards on the flow page.
 */
export const COMPLIANCE_REVIEW_EVENT_TYPES = [
  "compliance_review_flagged",
  "compliance_review_remark",
  "compliance_review_approved",
] as const;

export type ComplianceReviewEventType = (typeof COMPLIANCE_REVIEW_EVENT_TYPES)[number];

/**
 * Which process events become a feedback card on the flow page: the ones
 * carrying something a PERSON wrote — review decisions, the reason a flow was
 * returned to draft or cancelled, and remarks left on completed advice.
 *
 * Deliberately excludes operational failures (a failed PDF generation, a
 * failed or expired signing request). Those have recovery actions in Process
 * and show up as a stop, but they are not feedback and never raise the
 * counter. Review decisions that simply let the process continue are not
 * messages either.
 */
export const FLOW_PROCESS_MESSAGE_TYPES = [
  "compliance_rejected",
  "external_rejected",
  "drafted",
  "cancelled",
  "compliance_review_flagged",
  "compliance_review_remark",
] as const;

export type FlowProcessMessageType = (typeof FLOW_PROCESS_MESSAGE_TYPES)[number];

/**
 * One undismissed feedback card. `id` is the `flow_instance_event` id and is
 * what the dismiss endpoint takes.
 *
 * "Unread" is the same thing as "undismissed" and is shared for the whole
 * flow: viewing the messages or opening Process does not clear it, and only
 * an explicit dismissal does.
 */
export type FlowProcessMessage = {
  id: string;
  type: FlowProcessMessageType;
  /** Null renders as "No comment provided" — never invent one. */
  comment: string | null;
  /** Recorded author, where one exists. Never invented. */
  actorName: string | null;
  /** Step the flow left, for messages recorded on a backward move. */
  fromStatus: FlowInstanceStatus | null;
  createdAt: string;
  /** Predates the current submission — shown as "Previous submission". */
  historical: boolean;
};

/**
 * Why a process is stopped rather than simply waiting. Ordinary waiting — for
 * compliance, for an external decision, for a signature — is not stopped.
 */
export const FLOW_PROCESS_STOP_REASONS = [
  "compliance_rejected",
  "external_rejected",
  "cancelled",
  "pdf_failed",
  "signing_failed",
] as const;

export type FlowProcessStopReason = (typeof FLOW_PROCESS_STOP_REASONS)[number];

export type FlowProcessStop = {
  reason: FlowProcessStopReason;
  /**
   * When the stop was recorded. Null when nothing recorded a time for it —
   * the activity-log write is fire-and-forget, so the event backing a stop can
   * legitimately be missing. Never an empty string pretending to be a date.
   */
  sinceAt: string | null;
};

/**
 * Why reopening is not offered.
 *
 *   completed  the process finished; completed advice is final
 *   signed     a document has been signed — the client's act is never unwound
 *   editable   the flow is already editable, so there is nothing to reopen
 */
export const REOPEN_BLOCKED_REASONS = ["completed", "signed", "editable"] as const;

export type ReopenBlockedReason = (typeof REOPEN_BLOCKED_REASONS)[number];

/** What reopening this flow would remove. Every count is 0 when nothing applies. */
export type FlowReopenCleanup = {
  pdfCount: number;
  activeSignatureCount: number;
  hasPendingCompliance: boolean;
  hasPendingExternal: boolean;
  draftOrderCount: number;
};

export type FlowReopenState = {
  available: boolean;
  /** Set whenever `available` is false; the UI shows it rather than hiding silently. */
  blockedReason: ReopenBlockedReason | null;
  /**
   * Which endpoint reopening calls. A cancelled flow never entered the process
   * and takes the short path (`reopen`, no cleanup, no reason field); anything
   * else unwinds the process it reached (`draft`).
   */
  mode: "reopen" | "draft";
  cleanup: FlowReopenCleanup;
};

/**
 * Body of POST /internal/flow-instances/:id/process/reopen. `reason` (max 500)
 * is recorded as the comment on the `reopened` activity event.
 */
export type ReopenFlowInstanceRequest = {
  reason?: string | null;
};

/** Response of POST /internal/flow-instances/:id/process/messages/:messageId/dismiss. */
export type DismissProcessMessageResponse = {
  id: string;
  dismissed: true;
};

/** Read model for the process page: step sequence + current position. */
export type FlowProcessState = {
  instanceId: string;
  status: FlowInstanceStatus;
  steps: FlowInstanceStatus[];
  cancelledAt: string | null;
  completedAt: string | null;
  /** Existing external-status field (executed/canceled) — drives external review. */
  externalStatus: string | null;
  externalStatusInfo: string | null;
  flags: {
    requireComplianceApproval: boolean;
    requireExternalApproval: boolean;
    enableEidSigning: boolean;
    enableDigitalSigning: boolean;
  };
  approvals: FlowInstanceApproval[];
  /** Full chronological audit log of process actions (newest first). */
  events: FlowProcessEvent[];
  /**
   * QL-399 — undismissed feedback, newest first. A subset of `events`: the
   * ones a person has to see, minus the ones already dismissed for the flow.
   */
  messages: FlowProcessMessage[];
  /** Null while the process is merely waiting or running normally. */
  stopped: FlowProcessStop | null;
  reopen: FlowReopenState;
};

/**
 * QL-21 — which instances of a flow get the compliance approval step, once
 * `requireComplianceApproval` puts it in play.
 *
 *   all            every instance (the historic behaviour)
 *   screening_hit  only where the client's screening standing needs attention
 *                  (`screeningRequiresCompliance`: needs_review, blocked or
 *                  incomplete) — clean and never-screened clients, accepted
 *                  hits and not-a-match hits skip the step; see
 *                  `requiresComplianceApproval`
 */
export const COMPLIANCE_APPROVAL_SCOPES = ["all", "screening_hit"] as const;

export type ComplianceApprovalScope = (typeof COMPLIANCE_APPROVAL_SCOPES)[number];

// --- Flow Instance ---

export type FlowInstance = {
  id: string;
  flowId: string;
  clientId: string;
  status: string;
  name: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  publicToken: string | null;
  publicTokenExpiry: string | null;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

// --- Goal ---

export type GoalCustomProduct = {
  id: string;
  goalId: string;
  productId: string;
  amount: number;
  // Whole units for "units"-type products (null for amount-type). The monetary
  // `amount` is the estimated value derived from units × last price.
  units: number | null;
  weight: number;
  order: number;
  createdAt: string;
  updatedAt: string;
  product?: Record<string, unknown>;
};

// --- Dynamic portfolio generation (QP-185 flow-side) ---

/** The goal-portfolio type allow-list — the server-side validation source. */
export const PORTFOLIO_TYPES = ["model", "custom", "dynamic"] as const;
export type PortfolioType = (typeof PORTFOLIO_TYPES)[number];

/**
 * QL-198 — why a MODEL portfolio resolved nothing. Persisted on the risk
 * assessment result, carried on completion steps and on the portfolio
 * composition, so it is a contract between services and every surface rather
 * than a detail of the resolver.
 *
 * The model-path counterpart of `DynamicGenerationErrorReason` below.
 */
export type ModelResolutionFailureReason =
  /** The goal has no risk score yet — the risk assessment has not been submitted. */
  | "no_risk_score"
  /**
   * No active portfolio in the collection carries this risk score — or, for a goal
   * without a platform, no untagged one does (QL-419: tagged portfolios never serve
   * a platform-less goal).
   */
  | "no_portfolio_for_score"
  /**
   * Portfolios exist at this risk score, but none is tagged to the goal's selected
   * platform. QP-226 removed the untagged fallback, so this is a configuration gap
   * the advisor cannot fix from inside the flow.
   */
  | "no_portfolio_for_platform";

export type DynamicGenerationErrorReason =
  | "no_risk_assessment"
  | "no_dynamic_portfolio_for_score"
  /**
   * Dynamic portfolios exist at this risk score, but none is tagged to the
   * goal's selected platform (QL-584) — a configuration gap, as
   * `no_portfolio_for_platform` is on the model path.
   */
  | "no_dynamic_portfolio_for_platform"
  /**
   * The matching dynamic portfolio has no products or its weights do not total
   * 100 — a configuration gap a designer closes in the collection.
   */
  | "dynamic_portfolio_incomplete"
  /**
   * The client chose a preference no active product carries on the goal's
   * platform (QL-584) — a configuration gap. `errorPreference` names it.
   */
  | "no_product_for_preference"
  /**
   * Products carry the chosen preference on the goal's platform, but the
   * client is not eligible for any of them (QL-584). `errorPreference` names it.
   */
  | "no_eligible_product_for_preference"
  /**
   * The composition would hold a product that does not match the client's
   * sustainability preferences and no matching product can take its place
   * (QL-657): no portfolio is generated. `errorSustainabilityCriteria` lists the preferences.
   */
  | "sustainability_not_matched";

/** One failed soft criterion on a generated position. */
export type GeneratedPositionFailedCriterion = {
  kind: "sustainability";
};

/** A product named on a generated position's note — the name is frozen at generation time. */
export type GeneratedPositionProductRef = {
  productId: string;
  productNameI18n: LocalizedString;
};

/**
 * One base product this position stands in for (QL-584): the client is not
 * eligible for it or it does not match the sustainability preferences, and
 * this position's product has the closest asset allocation. The cause is not
 * recorded. With partial replacement the base product keeps part of its weight.
 */
export type GeneratedPositionReplacement = GeneratedPositionProductRef & {
  /** Sum of absolute allocation differences, 0 (identical) to 200 (nothing in common). */
  distance: number;
  /** The other eligible products at the same distance, in tie order. */
  alternatives: GeneratedPositionProductRef[];
  /**
   * Partial replacement moved only part of the base product's weight here:
   * the base product is still a position with the rest. Absent when the whole
   * weight moved.
   */
  partial?: true;
};

/** A client preference named on generation output — the name is frozen at generation time. */
export type GeneratedPreferenceRef = {
  preferenceId: string;
  preferenceNameI18n: LocalizedString;
};

/**
 * One chosen client preference this position serves (QL-584): its product
 * received the preference's whole share of the portfolio. The product is the
 * first eligible one matching the client's sustainability criteria, or the
 * first eligible one when none matches.
 */
export type GeneratedPositionPreference = GeneratedPreferenceRef & {
  /** The other eligible products carrying the preference, earliest created first. */
  alternatives: GeneratedPositionProductRef[];
};

export type GeneratedPositionAnnotations = {
  failedCriteria: GeneratedPositionFailedCriterion[];
  /** One entry per replaced base product; a merged position carries several. */
  replaces?: GeneratedPositionReplacement[];
  /**
   * The client is not eligible for this base product and no eligible product
   * shares an asset class with it. The position stays at its weight and the
   * flow cannot be submitted.
   */
  unsuitableNoReplacement?: true;
  /** One entry per chosen preference the position serves. */
  preferences?: GeneratedPositionPreference[];
};

/** Inputs the last generation was computed from (regeneration/diff basis). */
export type DynamicGenerationBasis = {
  riskScore: number | null;
  platformId: string | null;
  /**
   * The goal's time horizon, an input to product suitability. Absent on a
   * basis recorded before QL-584, which reads as "none".
   */
  timeHorizonId?: string | null;
  /**
   * Stable hash of the client's sustainability / client-preference /
   * product-assessment answers and classification.
   */
  answersHash: string;
};

/**
 * Outcome of a dynamic-portfolio regeneration fan-out — returned as
 * `dynamicSync` by every endpoint that triggers regeneration so callers can
 * invalidate the affected portfolio queries.
 */
export type DynamicRegenSync = {
  changed: boolean;
  goalIds: string[];
  portfolioIds: string[];
};

/**
 * Generation metadata persisted on the goal portfolio. `status: "error"` always
 * comes with zero generated rows; the reason drives the explanatory UI banner.
 */
export type DynamicGenerationMeta = {
  status: "ok" | "error";
  errorReason?: DynamicGenerationErrorReason;
  /** The chosen preference that could not be served — set with the two preference reasons. */
  errorPreference?: GeneratedPreferenceRef;
  /** The client's sustainability criteria — set with `sustainability_not_matched` (QL-657). */
  errorSustainabilityCriteria?: SustainabilityCriterionDescriptor[];
  basis: DynamicGenerationBasis;
  generatedAt: string;
};

/** Read-surface shape of one generated position (advisor + public flow). */
export type GeneratedPositionItem = {
  id: string;
  productId: string;
  productNameI18n: LocalizedString;
  isin: string | null;
  /** Set only on positions generated before QL-584 (one product per asset class). */
  assetClassId: string | null;
  assetClassNameI18n: LocalizedString;
  weight: number;
  order: number;
  annotations: GeneratedPositionAnnotations | null;
};

/**
 * Request body for creating a goal (internal advisor + public client surfaces).
 * All fields optional — the `createGoal` service fills defaults (`name` →
 * "Goal N" / "Mål N" in the flow language, `goalType` → "other", schedule →
 * one-time).
 */
export type CreateGoalRequest = {
  name?: string;
  goalType?: string;
  /** QL-281 — optional free-text description, max 150 chars (same cap as update). */
  description?: string | null;
  amount?: number;
  timeHorizonId?: string;
  riskLevelId?: string;
  portfolioId?: string;
  /**
   * QL-212 — language for the snapshots built when the new goal inherits the
   * client-level risk answer. Defaults to `en_GB`.
   */
  lang?: string;
};

/**
 * QL-212 (D13) — what the client-level risk answer did to a goal the moment it
 * was created. A new goal is never born blank under a global scope: creation
 * ends by applying the instance's stored answer to it, exactly as the fan-out
 * does, so an `applied` outcome means the goal already has its score and its
 * model portfolios resolved.
 *
 * `pending` is the ordinary answer under a mixed or all-per-goal model — the
 * goal's own page has not been answered yet — and nothing was written, so the
 * caller has nothing to invalidate beyond the goals list.
 */
export type GoalRiskInheritance = {
  outcome: RiskAssessmentGoalOutcome;
  /** Portfolio-scoped snapshot invalidations, empty unless the goal was scored. */
  refreshHints: SnapshotInvalidation[];
  /** QP-185 — set when applying regenerated the goal's dynamic portfolios. */
  dynamicSync?: DynamicRegenSync;
};

/**
 * Response of a goal create. The goal itself, plus — when the flow's risk model
 * captures anything at client level — what the inherited answer produced.
 * `Goal` already carries the score and `hasRiskAssessment`, so `riskInheritance`
 * exists for the cache: it names the queries a scored-at-birth goal moved.
 */
export type CreateGoalResponse = Goal & {
  riskInheritance?: GoalRiskInheritance;
};

/**
 * Risk-score fields come in two kinds:
 *
 * - **Frozen** — written when the risk assessment is submitted and never
 *   recomputed on read: `riskScore`, `riskScoreRaw`, `riskScoreAdjustment`,
 *   `riskScoreCap`, `isRiskScoreCapped`. Later changes to the risk model or the
 *   client's cap field do not touch them.
 * - **Derived** — computed by the goal service on every read, never stored:
 *   `riskScoreCapState` (frozen `isRiskScoreCapped` + the live risk model and
 *   client custom field).
 */
export type Goal = {
  id: string;
  flowInstanceId: string;
  name: string;
  goalType: string;
  description: string | null;
  hasRiskAssessment: boolean;
  riskScore: number | null;
  // QP-180 — score decomposition: riskScore stays the FINAL effective score.
  riskScoreRaw: number | null;
  // QL-8 — raw decimal adjustment sum; readers truncate toward zero before
  // display (final = clamp(raw + trunc(adjustment)), then cap).
  riskScoreAdjustment: number;
  /** Frozen — the cap level that applied to this client at submit, or null. */
  riskScoreCap: number | null;
  /**
   * Frozen — true iff `riskScoreCap` actually lowered the score at submit (a
   * cap can apply without biting).
   */
  isRiskScoreCapped: boolean;
  /**
   * Derived — QL-71's goal-scoped `__goal_risk_score_capped__` conditional
   * source, computed server-side by the goal service (never by a reader).
   */
  riskScoreCapState: RiskScoreCapState;
  /** QP-195 — the goal's selected platform row id (null = none). */
  platformId: string | null;
  /** QL-281 — the selected platform's label, null when none is selected. */
  platform: { id: string; labelI18n: Record<string, string> | null } | null;
  /**
   * QP-150 — schedule, amounts and portfolio type are portfolio-scoped; the goal
   * carries only this slim per-portfolio subset (ordered by portfolio order).
   */
  portfolios: {
    id: string;
    portfolioType: string;
    scheduleType: string;
    /**
     * QL-774 — derived server-side: the portfolio continues a bank account or
     * savings agreement. Feeds the `__followup_mode__` conditional source.
     */
    isFollowup: boolean;
  }[];
  timeHorizonId: string | null;
  riskLevelId: string | null;
  sourceGoalId: string | null;
  supersededAt: string | null;
  order: number;
  tenantId: string;
  createdById: string | null;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
  timeHorizon: Record<string, unknown> | null;
  riskLevel: Record<string, unknown> | null;
  /**
   * QL-664 — a mandate template resolves for this goal's platform and risk
   * score: only custom portfolios may be added (model / dynamic are refused;
   * an existing one is flagged by the completion check). Server-derived.
   */
  templateRestricted: boolean;
};

// --- Withdrawal ---

export type WithdrawalItem = {
  id: string;
  name: string;
  scheduleType: "one_time" | "recurring";
  oneTimeAmount: number | null;
  oneTimeDate: string | null;
  recurringAmount: number | null;
  recurringFrequency: "monthly" | "quarterly" | "yearly" | null;
  recurringStartDate: string | null;
  recurringEndDate: string | null;
  order: number;
};

// --- Flow Instance Metadata (single table, no joins) ---

export type FlowInstanceMetadataResponse = {
  id: string;
  name: string | null;
  flowId: string;
  flowName: string | null;
  clientId: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  publicToken: string | null;
  publicTokenExpiry: string | null;
  /**
   * QL-505 security follow-up — a revoke KEEPS `publicToken` set (see
   * `isPublicLinkLive` in `public-link.ts`), so `publicToken` alone
   * can no longer tell a caller whether the link still works. Null means the
   * link (if any) is live; a reason (`"revoked"` | `"representative_removed"`)
   * means it is not — the Invite Client modal reads this to show "needs a
   * new link" instead of a stale, non-working one.
   */
  publicAccessRevokedReason: string | null;
  inviteSentAt: string | null;
  inviteSentVia: string | null;
  inviteSentTo: string | null;
  currentPageId: string | null;
  currentGoalId: string | null;
  completedPageIds: string[];
  progressPercent: number;
  lastActiveAt: string | null;
  externalStatus: string | null;
  externalStatusInfo: string | null;
  needsSnapshotRegeneration: boolean;
  /**
   * QL-675 — a rebuild of the stale snapshots is queued or running in the
   * worker. The wizard shows an "updating" notice instead of the Regenerate
   * button and polls `GET …/snapshot-status` until it ends.
   */
  snapshotsRegenerating: boolean;
  /**
   * QL-260 / QL-737 — where a partner system wants the advisor's browser sent
   * back to (absolute http(s) URL, validated by the API). Only applies when the
   * advisor works in the web app in a flow opened with the standalone
   * `launchUrl`: they are returned when the process reaches a hand-off step
   * (review or completed) or when they cancel the advice in the web app.
   * Cancelling through the API or from another session moves nobody's browser.
   * Advisor flow only; never exposed on the public client flow.
   */
  internalRedirectUrl: string | null;
  /** QL-239 — the flow's status (inactive / preview / active). */
  flowStatus: FlowStatus;
  /**
   * QL-239 — true when the flow is in preview and the viewer is not a
   * designer: they may open and edit the instance but not submit it.
   */
  submitBlockedByPreview: boolean;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * QL-675 — where an instance's snapshots stand. `needsSnapshotRegeneration`:
 * stale (Submit is refused). `snapshotsRegenerating`: a rebuild is queued or
 * running in the worker — wait for it; when it ends with the flag still set,
 * the rebuild failed and Regenerate is offered again.
 */
export type SnapshotRegenerationStatusResponse = {
  needsSnapshotRegeneration: boolean;
  snapshotsRegenerating: boolean;
};

/**
 * QL-675 — the Regenerate button queues a rebuild and returns at once; follow
 * it through `SnapshotRegenerationStatusResponse`. `queued` is false for a
 * locked instance, whose snapshots are never rebuilt.
 */
export type RegenerateSnapshotsResponse = SnapshotRegenerationStatusResponse & {
  queued: boolean;
};

// --- Flow Definition (wizard-transformed, cached per session) ---

export type FlowDefinitionResponse = {
  flowId: string;
  name: string;
  description: string | null;
  riskModelId: string | null;
  portfolioCollectionId: string | null;
  currency: string;
  language: string;
  multiGoal: boolean;
  /** QL-593 — goal cap per instance while multiGoal is on. */
  maxGoals: number;
  cancelReasonMode: CancelReasonMode;
  fields: unknown[];
  pages: unknown[];
  riskModel: unknown | null;
  financialSituationModel: unknown | null;
  portfolioCollection: unknown | null;
};

// --- Field Values (form data only, no snapshots) ---

export type FieldValuesResponse = {
  values: Array<{
    id: string;
    flowFieldId: string;
    value: string;
    goalId: string | null;
    portfolioId: string | null;
  }>;
};

/**
 * Response of PATCH /internal/flow-instances/:id/field-values and its public
 * twin POST /public/flow/field-values. A locked instance is a 409, not this.
 */
export type SaveFieldValueResponse = {
  success: boolean;
  flowFieldId?: string;
  value?: string;
  goalId?: string | null;
  portfolioId?: string | null;
  error?: string;
};

// --- Instance Snapshots (snapshot values + aggregated cost/expected value) ---

export type SaveSnapshotResponse = {
  success: boolean;
  value?: string;
  error?: string;
};

// --- Individual Snapshot Responses ---

export type SnapshotValueResponse = {
  value: string | null;
};

// --- Component Value Responses (new FlowInstanceComponentValue table) ---

export type ComponentValueResponse = {
  inputData: string | null;
  calculatedData: string | null;
};

/**
 * QL-298 — the financial situation value with the currency it is denominated
 * in: the FLOW currency. `calculatedData` totals are in it, synced accounts
 * are converted into it, entered rows are assumed to already be in it.
 */
export type FinancialSituationValueResponse = ComponentValueResponse & {
  /** ISO 4217 — the flow's calculation currency. */
  currency: string;
};

export type SaveComponentValueResponse = {
  success: boolean;
  inputData?: string;
  calculatedData?: string;
  error?: string;
  /**
   * QL-298 — field-level detail on a validation failure (`success: false`):
   * which input field was rejected and why, for callers that surface errors
   * per field (the public API's `fields` array).
   */
  fields?: Array<{ path: string; message: string }>;
  /**
   * QL-212 — a stable identifier for the RULE a refusal hit, so a
   * caller can branch on the cause instead of matching on wording. Absent
   * unless the rule has one of its own (e.g. `risk_input_scope_mismatch`,
   * `risk_captured_per_goal`, `risk_captured_per_client`).
   */
  code?: string;
  /**
   * QP-195 — set when the save triggered a goal-platform re-evaluation that
   * changed something (platform auto-applied/cleared, model portfolios
   * re-resolved, custom products removed). Callers should invalidate goal +
   * snapshot queries and surface `removedProducts` as a warning.
   */
  platformSync?: PlatformSyncResult;
  /**
   * QP-179 — set when the save triggered a sustainability matching recompute
   * that changed something (snapshots regenerated/cleared, stale decision
   * voided). Callers should invalidate the hinted snapshot queries + goal
   * sustainability state.
   */
  sustainabilitySync?: { changed: boolean; refreshHints: SnapshotInvalidation[] };
  /**
   * QP-185 — set when the save regenerated the instance's dynamic portfolios.
   * Callers should invalidate the hinted portfolio queries.
   */
  dynamicSync?: DynamicRegenSync;
};

// --- Client Preferences component (QP-224) ---

/**
 * QP-224 — input for the `client_preferences` flow component (global scope).
 * Keys are `client_preference.id`s of the flow's portfolio collection; values
 * are the client's explicit yes/no answers. Unanswered preferences are simply
 * absent — there is no default.
 */
export type ClientPreferencesComponentInput = {
  answers: Record<string, boolean>;
};

/** QP-224 — server-normalized `calculatedData` blob for `client_preferences`. */
export type ClientPreferencesComponentResult = {
  answers: Record<string, boolean>;
  lastUpdatedAt: string;
};

/** QP-195 — outcome of a server-side goal-platform sync/change cascade. */
export type PlatformSyncResult = {
  /** True when at least one goal's platform or portfolio resolution changed. */
  changed: boolean;
  refreshHints: SnapshotInvalidation[];
  /** Names of custom products removed because the new platform excludes them. */
  removedProducts: string[];
  /**
   * QL-72 — names of model portfolios whose hand pick the platform change
   * cleared (returned to Automatic) because the new platform does not offer the
   * picked portfolio. Picks still on offer are kept and not listed.
   */
  resetModelSelections: string[];
  /** QP-185 — set when the platform change regenerated dynamic portfolios. */
  dynamicSync?: DynamicRegenSync;
};

/** Component types whose value can be reused from a client's previous completed flow instance. */
export const REUSABLE_COMPONENT_TYPES = {
  CLIENT_CLASSIFICATION: "client_classification",
  PRODUCT_ASSESSMENT: "product_assessment",
  SUMSUB_VERIFICATION: "sumsub_verification",
} as const;
export type ReusableComponentType =
  (typeof REUSABLE_COMPONENT_TYPES)[keyof typeof REUSABLE_COMPONENT_TYPES];
/** All reusable component types, for iteration/validation. */
export const REUSABLE_COMPONENT_TYPE_VALUES = Object.values(REUSABLE_COMPONENT_TYPES);

// --- Reuse Previous Answer (unified internal endpoints) ---

/** Machine-readable verdict reason from the reuse resolver (see flow-reuse-resolver-service). */
export const REUSE_REASONS = {
  OK: "ok",
  INSTANCE_NOT_FOUND: "instance-not-found",
  INSTANCE_COMPLETED: "instance-completed",
  NOT_ON_FLOW: "not-on-flow",
  ALREADY_FILLED: "already-filled",
  NOTHING_TO_REUSE: "nothing-to-reuse",
} as const;
export type ReuseReason = (typeof REUSE_REASONS)[keyof typeof REUSE_REASONS];

/** GET availability for a single reusable component type. */
export type ReuseAvailabilityResponse = {
  available: boolean;
  sourceDate: string | null;
};

/** POST apply request body. */
export type ApplyReuseRequest = {
  componentType: ReusableComponentType;
  /**
   * QL-505 — product_assessment only: required for a group without a
   * representative, rejected for every other client (enforced by
   * `product-assessment-service`). Ignored by the other component types.
   */
  memberId?: string;
  /** QL-654 — preferred over `memberId`: the answering party's `client_party.id` or `client_group_member.id`. */
  subjectId?: string;
};

/** POST apply success (200). */
export type ApplyReuseResponse = {
  applied: true;
  componentType: ReusableComponentType;
  sourceDate: string | null;
  value: { inputData: string | null; calculatedData: string | null };
  /** Present when applying the answer regenerated dynamic portfolios. */
  dynamicSync?: DynamicRegenSync;
};

/** POST apply refusal (409/404) — carries the machine-readable reason. */
export type ReuseRefusalResponse = {
  reason: Exclude<ReuseReason, typeof REUSE_REASONS.OK>;
};

// --- Client Classification Types ---

export type ClientClassificationType = "Retail" | "Professional" | "Eligible Counterparty";

export type ClientClassificationInput = {
  /**
   * @deprecated Ignored on save. The client record decides which rules apply —
   * a caller cannot classify a company under the retail opt-up test by asking
   * for it. Still present on the STORED value, where it records which branch
   * the result was computed on.
   */
  clientType?: "Person" | "Company";
  personAnswers?: {
    wantsOptUp: boolean;
    hasSignificantTransactions: boolean;
    hasLargePortfolio: boolean;
    hasFinanceExperience: boolean;
  };
  companyAnswers?: {
    isFinancialInstitution: boolean;
    hasLargeBalanceSheet: boolean;
    hasLargeRevenue: boolean;
    hasLargeEquity: boolean;
  };
};

export type ClientClassificationResult = {
  classificationType: ClientClassificationType;
  clientType: "Person" | "Company";
  determinedAt: string;
  personAnswers?: {
    optUpCriteriaMet: number;
  };
  companyAnswers?: {
    largeCriteriaMet: number;
  };
};

export type ClientClassificationPreviousAnswer = {
  inputData: string;
  calculatedData: string;
  timestamp: string | null;
} | null;

/**
 * QL-662 — GET …/client-classification/previous on the PUBLIC API: both payloads
 * decoded (objects, not JSON text), `timestamp` as an ISO instant, and the flow
 * instance the answer was taken from.
 */
export type ClientClassificationPreviousAnswerResponse = {
  inputData: unknown;
  calculatedData: unknown;
  timestamp: string;
  sourceFlowInstanceId: string;
} | null;

// --- Product Assessment Types ---

/**
 * One quiz attempt, kept for traceability (stored in inputData, so visible to
 * the client — it therefore carries only the drawn question ids and the
 * client's own selections, never the option scores).
 */
export type ProductAssessmentQuizAttempt = {
  attemptNumber: number;
  startedAt: string;
  completedAt: string | null;
  /** The server-drawn question ids for this attempt. */
  questionIds: string[];
  /** Filled at submit: the client's selected option per drawn question. */
  answers: Array<{ questionId: string; selectedOptionId: string }>;
  /** Sum of selected option scores, graded server-side. Null while in flight. */
  score: number | null;
  /** Null while the attempt is in flight. */
  passed: boolean | null;
};

export type ProductAssessmentQuizState = {
  attemptsCount: number;
  lastAttemptAt: string | null;
  /** A pass locks the quiz permanently. */
  passed: boolean;
  attempts: ProductAssessmentQuizAttempt[];
};

/**
 * One assessment mode answered OUTSIDE this flow, recorded as an
 * outcome rather than replayed as answers. The integrating system put the
 * client through its own quiz/questionnaire and reports only the result.
 *
 * Deliberately NOT part of `quizStates`: a pass there locks the quiz forever
 * (`getQuizAttemptAvailability`), which would make retaking it in the flow
 * impossible. Kept apart, attempts stay fully available and the in-flow answer
 * always supersedes this record — see `computeProductTypeVerdict`.
 */
export type ProductAssessmentExternalResult = {
  /** The outcome the external system reported. */
  passed: boolean;
  /** When the client completed it externally — the caller's fact, validated not to be in the future. */
  completedAt: string;
  /** The external system or process that ran it. Shown in the advice PDF. */
  source: string;
  /** The external system's own id for the attempt, for reconciliation. */
  reference?: string | null;
  /** Server clock at the moment of recording. Never taken from the caller. */
  recordedAt: string;
};

/**
 * Server-authored stored blob of the product_assessment component value
 * (`inputData`). Never accepted as a request body — every write goes through
 * `DeclareExperiencesRequest`, `SubmitQuestionnaireRequest`,
 * `SubmitQuizAttemptRequest`, `RecordExternalResultRequest` or
 * use-previous-answer; clients only parse it.
 */
export type ProductAssessmentStoredValue = ProductAssessmentSubjectInput & {
  /**
   * QL-505 — a group WITHOUT a representative: every member answers for
   * themselves, keyed by `client_group_member.id`. Each entry has exactly the
   * single-subject shape (own declarations, questionnaire answers, quiz
   * attempts/cooldown/lock and external results). While this is in use the
   * top-level single-subject fields stay empty; the group's combined position
   * lives in calculatedData. Entries of members who have since left are kept
   * (they are the record) but no longer count.
   */
  members?: Record<string, ProductAssessmentSubjectInput>;
};

/** One answering subject's product-assessment answers — the pre-QL-505 blob shape. */
export type ProductAssessmentSubjectInput = {
  experiences: Array<{
    productTypeId: string;
    hasExperience: boolean;
    /**
     * QL-505 — declaration-only types: who last ticked (or unticked) the box,
     * as they stood then. Absent/null while never declared. This is the
     * additive contract change S1 made: it lives next to `hasExperience`
     * rather than in a new top-level map, because it must survive untouched
     * across every recompute of the OTHER product types, exactly like
     * `hasExperience` already does.
     */
    declaredBy?: AnsweringPartyRecord | null;
  }>;
  /**
   * QUESTIONNAIRE answers, keyed by productTypeId then questionId → selected
   * option id. One entry per product type whose assessment is a questionnaire.
   */
  questionnaireAnswers?: Record<string, Record<string, string>>;
  /** QUIZ state (server-managed), keyed by productTypeId. */
  quizStates?: Record<string, ProductAssessmentQuizState>;
  /**
   * Externally answered modes, keyed by productTypeId then mode. A
   * product type with both modes may have one recorded here and the other
   * taken in the flow; the AND verdict still decides the category.
   */
  externalResults?: Record<
    string,
    Partial<Record<ProductTypeAssessmentMode, ProductAssessmentExternalResult>>
  >;
};

/** One assessment mode's outcome for a category, denormalized into calculatedData. */
export type ProductAssessmentOutcome = {
  mode: ProductTypeAssessmentMode;
  /** Summed score across the asked questions; null = not yet answered. */
  score: number | null;
  /** The pass bar the score is compared against. */
  minScore: number;
  passed: boolean;
  /**
   * Per-mode status stored at save time. The QUIZ status is a function of
   * `now` at save (cooldown), so the widget overlays live attempt availability
   * on top of it; backend readers use `hasExperience`/`passed` only.
   */
  status: AssessmentModeStatus;
  /**
   * The questions the client answered with their selected answer, both resolved
   * and localized at save time. Used by the PDF to show the actual Q&A. Empty
   * when nothing has been answered yet, and always empty for an externally
   * recorded mode — there are no answers of ours to show.
   */
  answeredQuestions?: Array<{ question: string; answer: string }>;
  /**
   * Which answer governed this outcome. `"external"` means no answer
   * was given in this flow and a recorded external result decided it; answering
   * in the flow flips this back to `"flow"`. Absent on rows written before
   * this feature, which are always flow answers.
   */
  resultSource?: ProductAssessmentResultSource;
  /**
   * The external record that governed, present only when
   * `resultSource` is `"external"`. Disclosed in the advice PDF so the document
   * never implies the client answered our questions.
   */
  external?: {
    source: string;
    completedAt: string;
    reference?: string | null;
  };
  /**
   * When the client completed the answer that decided this outcome (QL-427):
   * the passing quiz attempt (otherwise the last completed one), the
   * questionnaire's `answerScores` `answeredAt` (when the current answers were
   * given; carried-over answers keep the earlier flow's date), or
   * `external.completedAt`. Null while nothing is answered, and for a
   * questionnaire answered before answer times were recorded until it is
   * recorded — then it is the recording time, the latest the answers can have
   * been given. Absent on rows saved before the field existed;
   * the service fills it in on read — see `withOutcomeCompletedAt`.
   */
  completedAt?: string | null;
  /**
   * QL-505 — who gave the answer that decided this outcome, as they stood when
   * they gave it. Null while unanswered and for an external result (the
   * integrator attested it; there is no answering party of ours). Absent on
   * rows written before QL-505. On a group aggregate row it is absent — see
   * `memberSummary` and `ProductAssessmentResult.memberResults`.
   */
  answeredBy?: AnsweringPartyRecord | null;
  /**
   * QL-505 — only on the group aggregate of a group without a representative:
   * how many of the counted members passed this mode ("2 of 5 passed").
   * `passed` on the aggregate is true only when `passed === total && total > 0`.
   */
  memberSummary?: { passed: number; total: number };
};

/** One product type's result for one answering subject (the pre-QL-505 `experiences[]` item). */
export type ProductAssessmentExperienceResult = {
  productTypeId: string;
  productTypeName: string;
  /**
   * Category verdict. Legacy declaration for product types without an
   * assessment; AND across all enabled assessment modes (every mode's summed
   * score >= its minScore) for product types with one or more. Suitability,
   * completion and PDF all key off this flag.
   */
  hasExperience: boolean;
  /**
   * Server-stored tri-state category verdict (AND across enabled modes);
   * null for declaration-only types. The widget renders the badge from this
   * value. Rows written before QL-194 lack the field — they are not
   * recomputed and self-heal on the next assessment mutation.
   */
  verdict: ProductAssessmentVerdict | null;
  /**
   * One entry per enabled assessment mode, in config order. Present and
   * non-empty when the product type has at least one assessment configured;
   * omitted for simple (declaration-only) types.
   */
  assessments?: ProductAssessmentOutcome[];
  /**
   * QL-505 — declaration-only types: who ticked (or unticked) the box, as they
   * stood then. Null while never declared. On a group aggregate row it is absent
   * and `memberSummary` counts the members who declared experience.
   */
  declaredBy?: AnsweringPartyRecord | null;
  /**
   * QL-505 — on a group aggregate: declaration-only types "4 of 5 confirmed"; an assessed type
   * (QL-653) the number of lines that PASSED it, of the total, and `completed` — how many have
   * answered every mode of it (passed or failed), so "2 of 3 completed" can be said without
   * naming anyone. Counts only.
   */
  memberSummary?: { passed: number; total: number; completed?: number };
};

export type ProductAssessmentResult = {
  /**
   * The client's position per product type — what suitability, reference data,
   * dynamic generation, completion and the PDF headline read. For a group
   * without a representative this is the AGGREGATE: a type passes only when
   * every counted member passes it (nothing averaged, no majority — the weakest
   * member sets the position, ESMA35-43-3172 ¶70). Aggregate verdict: "passed"
   * when every member passed, "failed" when any member failed, else "incomplete";
   * a group with no members is "incomplete" / hasExperience false.
   */
  experiences: ProductAssessmentExperienceResult[];
  /** QL-505 — how this value was answered. Absent on rows written before QL-505 = "single". */
  answeringMode?: AnsweringMode;
  /**
   * QL-505 — present only when `answeringMode` is "members": one entry per
   * CURRENT member, in member order, each with that member's own results. A
   * member who has not answered anything still has an entry (all incomplete).
   */
  memberResults?: Array<{
    /**
     * The subject's slot key (QL-654: `client_party.id` for a representative,
     * `client_group_member.id` for a member). The name is kept from QL-505;
     * `subjectId` repeats it and is absent on rows written before QL-654.
     */
    memberId: string;
    subjectId?: string;
    name: string;
    /**
     * QL-653 — who answered for this line when it was computed: the `client_party.id` of the
     * member's POA/guardian, null when none. Lets the submit tell that a slot's answerer
     * changed with no line moving (a single POA expiring by date). Absent on older rows.
     */
    ownerPartyId?: string | null;
    experiences: ProductAssessmentExperienceResult[];
  }>;
  portfolioCollectionId: string;
  portfolioCollectionName: string;
  determinedAt: string;
};

/**
 * The score behind each answer of one answered assessment: a questionnaire, or
 * one COMPLETED quiz attempt.
 *
 * Never part of `inputData`/`calculatedData`. Those are read by the client's
 * own flow session, and an option's score next to a quiz that can be retaken is
 * the answer key — so this lives in the component value's server-only column
 * (`ComponentValueServerData`) and is returned only to callers trusted with it
 * (the public API's key holder).
 *
 * Recorded WHEN THE ANSWER IS GIVEN — a quiz attempt at submit, in the same
 * transaction that grades it; a questionnaire when it is submitted — from the
 * configuration as it was then. Editing or deleting the assessment, a question
 * or an option afterwards cannot rewrite it, and the answers always add up to
 * the score the attempt was graded on.
 */
export type ProductAssessmentAnswerScores = {
  /**
   * QL-505 — the party who answered, when several answer for the client: the
   * subject id (QL-654: a representative's `client_party.id` or a member's
   * `client_group_member.id`). Null for every other client.
   */
  memberId?: string | null;
  /** QL-654 — repeats `memberId` (the subject id). Absent on entries recorded before QL-654. */
  subjectId?: string | null;
  /**
   * QL-505 — who answered, as they stood when they answered (name, not
   * national ID). Null/absent for entries recorded before QL-505.
   */
  answeredBy?: AnsweringPartyRecord | null;
  productTypeId: string;
  mode: ProductTypeAssessmentMode;
  /** QUIZ: which attempt. Null for a QUESTIONNAIRE, which has no attempts. */
  attemptNumber: number | null;
  /**
   * QUIZ: when the attempt was completed. QUESTIONNAIRE: when it was answered —
   * for answers carried over from an earlier flow, when they were answered there
   * (that flow's completion if they predate recording); null for one never
   * recorded (see `recordedAt`).
   */
  answeredAt: string | null;
  /**
   * When this entry was recorded. Null = never recorded — an answer given before
   * recording existed — and therefore derived from the CURRENT configuration on
   * every read, so it can still change. Later than `answeredAt` for answers
   * carried over from an earlier flow, which are recorded on arrival.
   */
  recordedAt: string | null;
  answers: Array<{
    questionId: string;
    /** Empty string for a drawn quiz question the submission left unanswered. */
    selectedOptionId: string;
    /** Null when the question was no longer configured when this was recorded. */
    questionLabelI18n: LocalizedString | null;
    /** Null when unanswered, or the option was no longer configured. */
    selectedOptionLabelI18n: LocalizedString | null;
    /**
     * What the selected option contributed to the total. 0 when unanswered;
     * null when the question or option was no longer configured.
     */
    score: number | null;
    /** The best score any option of the question gives. Null when the question is gone or has no options. */
    maxScore: number | null;
  }>;
};

/**
 * `flow_instance_component_value.serverData` — what the server keeps about a
 * component value that must never travel with `inputData`/`calculatedData`,
 * both of which the client's own flow session reads. One key per component
 * type that needs it.
 */
export type ComponentValueServerData = {
  productAssessment?: {
    answerScores: ProductAssessmentAnswerScores[];
    /** QL-505 — the answering party (with national ID) of every answer event. Append-only. */
    answeringParties?: AnsweringPartyEvent[];
    /** QL-660 — see `CountedSlots`. */
    countedSlots?: CountedSlots;
  };
  /** QL-505 — the standalone knowledge quiz's answering parties (append-only); QL-528 — the per-answer scores of every graded attempt. */
  quiz?: {
    answeringParties: AnsweringPartyEvent[];
    /** QL-528. Absent on rows written before it existed — read as `[]`. */
    answerScores?: QuizAnswerScores[];
    /** QL-660 — see `CountedSlots`. */
    countedSlots?: CountedSlots;
  };
  /**
   * QL-668 — set only on a classification row the server recorded as the
   * automatic Retail default at submit (nobody answered). A later flow for the
   * same client applies the default again over such a row, but never over an
   * explicit answer; every explicit save clears it. Absent on rows written
   * before it existed — those read as explicit.
   */
  clientClassification?: { recordedDefault: true };
};

/**
 * QL-660 — who each answering line counted answers from at the LAST submission,
 * keyed by subject id; written in the submit transaction. A submitted instance's
 * reads count against it, not against the live parties, so they agree with the
 * frozen verdict whatever happened to the parties since. Absent on values
 * submitted before it existed (reads then keep their earlier behaviour); ignored
 * while the instance is editable.
 */
export type CountedSlots = Record<string, FrozenSlotIdentity>;

/**
 * QL-505 — `GET /api/v1/flow-instances/{id}/product-assessment/members[/{memberId}]`:
 * the per-member audit trail of a group without a representative.
 */
export type ProductAssessmentMemberView = {
  /** The subject's id (a `client_party.id` or `client_group_member.id`, QL-654); `subjectId` repeats it. */
  memberId: string;
  subjectId: string;
  name: string;
  /** False = the member has since left the group (or the representative was removed); their answers are kept but no longer count. */
  isCurrentMember: boolean;
  /** QL-660 — current member: who answers for them now; a member who left: as recorded on their last answer, else null. */
  memberRepresentative: MemberRepresentative | null;
  /**
   * QL-653 — the `client_group_member.id` this line counts towards, so the lines
   * of a member with 2+ representatives group under their member: equal to
   * `subjectId` for an ordinary member line, the member's id on an expanded
   * line (the representative's own `subjectId`). Null for a client-level
   * representative and for an entry whose member has left.
   */
  groupMemberId: string | null;
  experiences: ProductAssessmentExperienceResult[];
  answerScores: ProductAssessmentAnswerScores[];
};

export type ProductAssessmentMembersResponse = {
  members: ProductAssessmentMemberView[];
};

// --- Product Assessment quiz endpoints (QUIZ-mode assessments only) ---

export type StartQuizAttemptRequest = {
  /**
   * QL-654 — whose answer this is, when several parties answer for the client
   * (a Group with no representative, or a client with 2+ representatives):
   * the `client_party.id` of a representative or the `client_group_member.id`
   * of a member. Required then, rejected (400) for every other client.
   * Preferred over `memberId`, which stays as an alias for a group member.
   */
  subjectId?: string;
  /** QL-505 — alias of `subjectId` for a group member. */
  memberId?: string;
  productTypeId: string;
};

/**
 * Plain per-category "has experience" declaration for product types with no
 * assessment configured (QL-194). A map so the AI tool can declare several
 * categories in one atomic call; the web sends a single entry. Declaring a
 * product type that has an assessment is a validation error.
 */
export type DeclareExperiencesRequest = {
  /**
   * QL-654 — whose answer this is, when several parties answer for the client
   * (a Group with no representative, or a client with 2+ representatives):
   * the `client_party.id` of a representative or the `client_group_member.id`
   * of a member. Required then, rejected (400) for every other client.
   * Preferred over `memberId`, which stays as an alias for a group member.
   */
  subjectId?: string;
  /** QL-505 — alias of `subjectId` for a group member. */
  memberId?: string;
  declarations: Record<string, boolean>;
};

/**
 * One category's QUESTIONNAIRE, submitted whole on "Done".
 * `answers` maps every question id of the assessment to the selected option
 * id — an incomplete map, an unknown option or a stray question id is a
 * validation error and nothing is written. Resubmission always overwrites.
 */
export type SubmitQuestionnaireRequest = {
  /**
   * QL-654 — whose answer this is, when several parties answer for the client
   * (a Group with no representative, or a client with 2+ representatives):
   * the `client_party.id` of a representative or the `client_group_member.id`
   * of a member. Required then, rejected (400) for every other client.
   * Preferred over `memberId`, which stays as an alias for a group member.
   */
  subjectId?: string;
  /** QL-505 — alias of `subjectId` for a group member. */
  memberId?: string;
  productTypeId: string;
  answers: Record<string, string>;
};

/** Drawn questions with option scores stripped. */
export type StartQuizAttemptResponse = {
  attemptNumber: number;
  questions: Array<{
    id: string;
    labelI18n: LocalizedString;
    options: Array<{ id: string; labelI18n: LocalizedString }>;
  }>;
};

export type SubmitQuizAttemptRequest = {
  /**
   * QL-654 — whose answer this is, when several parties answer for the client
   * (a Group with no representative, or a client with 2+ representatives):
   * the `client_party.id` of a representative or the `client_group_member.id`
   * of a member. Required then, rejected (400) for every other client.
   * Preferred over `memberId`, which stays as an alias for a group member.
   */
  subjectId?: string;
  /** QL-505 — alias of `subjectId` for a group member. */
  memberId?: string;
  productTypeId: string;
  answers: Array<{ questionId: string; selectedOptionId: string }>;
};

// --- Externally answered assessments ---

/**
 * Record one product type's assessment as answered OUTSIDE this flow: the
 * outcome and when it happened, instead of the questions and answers.
 *
 * Public API only — an attestation by an integrating system under an API key,
 * not a user action. Re-recording the same (productTypeId, mode) overwrites.
 * The record NEVER blocks taking the assessment in the flow, and the moment it
 * is taken there the in-flow answer supersedes this one.
 */
export type RecordExternalResultRequest = {
  /**
   * QL-654 — whose answer this is, when several parties answer for the client
   * (a Group with no representative, or a client with 2+ representatives):
   * the `client_party.id` of a representative or the `client_group_member.id`
   * of a member. Required then, rejected (400) for every other client.
   * Preferred over `memberId`, which stays as an alias for a group member.
   */
  subjectId?: string;
  /** QL-505 — alias of `subjectId` for a group member. */
  memberId?: string;
  productTypeId: string;
  /** Which of the type's configured assessments this result is for. */
  mode: ProductTypeAssessmentMode;
  passed: boolean;
  /**
   * When the client completed it externally, as an ISO-8601 instant with `Z` or
   * an offset — a bare date or an offset-less local time is refused. Must not be
   * in the future, beyond a minute of tolerated clock skew.
   */
  completedAt: string;
  /** The external system or process that ran it — shown in the advice PDF. */
  source: string;
  /** The external system's own id for the attempt. */
  reference?: string | null;
};

/**
 * What the endpoint returns — NOT `SaveComponentValueResponse`.
 *
 * The service hands the two component payloads back as the JSON TEXT it stored,
 * and a string inside a JSON body would have to be parsed a second time, so the
 * public surface decodes them first (`parsedComponentValue`). Hence the
 * deliberate divergence: these two are the decoded values, not the raw strings.
 */
export type RecordExternalResultResponse = {
  success: boolean;
  inputData: unknown;
  calculatedData: unknown;
};

export type SubmitQuizAttemptResponse = {
  passed: boolean;
  availability: QuizAttemptAvailability;
  /** QP-185 — set when the quiz outcome regenerated dynamic portfolios. */
  dynamicSync?: DynamicRegenSync;
};

export type ProductAssessmentPreviousAnswer = {
  inputData: string;
  calculatedData: string;
  timestamp: string | null;
} | null;

/**
 * QL-662 — GET …/product-assessment/previous on the PUBLIC API: the two
 * payloads decoded (objects, not JSON text; same shapes as
 * `ProductAssessmentStoredValue` / `ProductAssessmentResult`), `timestamp` as
 * an ISO instant, and the flow instance the answer was taken from. The
 * internal `ProductAssessmentPreviousAnswer` above keeps its JSON-text shape.
 */
export type ProductAssessmentPreviousAnswerResponse = {
  inputData: unknown;
  calculatedData: unknown;
  timestamp: string;
  sourceFlowInstanceId: string;
} | null;

/**
 * Product-type ids of the custom products currently held across the instance's
 * portfolios. Lets the product-assessment component flag categories whose
 * suitability assessment has not been passed but which have products in use.
 */
export type ProductTypesInUseResponse = {
  productTypeIds: string[];
};

// --- Quiz Component Types (QL-13) ---

/**
 * One quiz attempt. Stored in `inputData`, which is client-visible, so it
 * carries the drawn question ids and the client's own selections only — never
 * the option scores.
 */
export type QuizAttempt = {
  attemptNumber: number;
  startedAt: string;
  completedAt: string | null;
  /** The server-drawn question ids for this attempt. */
  questionIds: string[];
  /** Filled at submit: the client's selected option per drawn question. */
  answers: Array<{ questionId: string; selectedOptionId: string }>;
  /** Sum of selected option scores, graded server-side. Null while in flight. */
  score: number | null;
  /** Null while the attempt is in flight. */
  passed: boolean | null;
};

/**
 * An advisor clearing the gate by hand.
 *
 * Without this, a client who fails and exhausts `maxAttempts` can never submit
 * the flow — the quiz blocks completion and nothing else unblocks it. That is
 * a dead end no other component has, because `product_assessment` degrades to
 * `hasExperience: false` rather than hard-blocking. The override is the way
 * out, and it is recorded rather than silent because it ends up on a
 * compliance document.
 */
export type QuizOverride = {
  reason: string;
  /** User id of the advisor who granted it. */
  byUserId: string;
  /** Resolved at write time so the PDF needn't join to render it. */
  byUserName: string;
  at: string;
};

export type QuizState = {
  attemptsCount: number;
  lastAttemptAt: string | null;
  /** A pass locks the quiz permanently. */
  passed: boolean;
  attempts: QuizAttempt[];
  /** Set when an advisor cleared the gate by hand. Counts as passed. */
  override?: QuizOverride;
};

/**
 * Server-authored stored blob of the `quiz` component value (`inputData`).
 * Never accepted as a request body — every write goes through
 * `StartQuizRequest` / `SubmitQuizRequest`; clients only parse it.
 *
 * Keyed by quizId rather than by component, because
 * `flow_instance_component_value` allows exactly one global row per component
 * type per instance. Several `quiz` components on one flow therefore share
 * this row, the same way product assessment holds several product types in
 * one blob.
 */
export type QuizStoredValue = QuizSubjectInput & {
  /**
   * QL-505 — a group WITHOUT a representative: per-member quiz states and
   * external results, keyed by `client_group_member.id`. The top-level fields
   * stay empty while this is in use; see `ProductAssessmentStoredValue.members`.
   */
  members?: Record<string, QuizSubjectInput>;
};

/** One answering subject's quiz answers — the pre-QL-505 blob shape. */
export type QuizSubjectInput = {
  quizStates: Record<string, QuizState>;
  /**
   * QL-13 — results attested by an integrating system, keyed by quizId.
   *
   * Deliberately NOT folded into `quizStates`, for the two reasons product
   * assessment keeps `externalResults` apart: a `passed` written there locks
   * the quiz forever (`getQuizAttemptAvailability`), so the client could never
   * sit it in the flow; and precedence belongs in one place, which is
   * `buildQuizOutcome` — an in-flow attempt always wins.
   */
  externalResults?: Record<string, QuizExternalResult>;
};

/**
 * A quiz the client sat OUTSIDE this flow, reported by the system that ran it.
 *
 * The outcome and when, never the questions and answers: that system scored on
 * its own scale, and recording a score next to our `minScore` would invite a
 * comparison that means nothing. Public API only — an attestation made under
 * an API key, not a user action.
 */
export type QuizExternalResult = {
  /** The outcome the external system reported. */
  passed: boolean;
  /** When the client completed it there — the caller's fact, checked not to be in the future. */
  completedAt: string;
  /** The external system or process that ran it. Disclosed on the compliance document. */
  source: string;
  /** That system's own id for the attempt, for reconciliation. */
  reference?: string | null;
  /** Server clock at the moment of recording. Never taken from the caller. */
  recordedAt: string;
};

/**
 * Body of `POST /api/v1/flow-instances/{id}/quiz/external-result`.
 *
 * Re-recording the same quiz overwrites. The record never blocks taking the
 * quiz in the flow, and the moment it is taken there the in-flow result
 * supersedes this one.
 */
export type RecordQuizExternalResultRequest = {
  /**
   * QL-654 — whose answer this is, when several parties answer for the client
   * (a Group with no representative, or a client with 2+ representatives):
   * the `client_party.id` of a representative or the `client_group_member.id`
   * of a member. Required then, rejected (400) for every other client.
   * Preferred over `memberId`, which stays as an alias for a group member.
   */
  subjectId?: string;
  /** QL-505 — alias of `subjectId` for a group member. */
  memberId?: string;
  quizId: string;
  passed: boolean;
  /**
   * When the client completed it externally, as an ISO-8601 instant with `Z`
   * or an offset. Must not be in the future, beyond a minute of clock skew.
   */
  completedAt: string;
  /** The external system or process that ran it. Must not be blank. */
  source: string;
  reference?: string | null;
};

/** One quiz's outcome, denormalized into `calculatedData` for the PDF and completion. */
export type QuizOutcome = {
  quizId: string;
  /** Resolved at save time so the PDF can label the field without a join. */
  quizName: string;
  status: QuizResultStatus;
  /** Score of the last completed attempt; null when never attempted. */
  score: number | null;
  /** The pass bar the score was compared against. */
  minScore: number;
  /** ISO timestamp of the passing attempt; null unless status is "passed". */
  passedAt: string | null;
  /**
   * The questions of the last completed attempt with the client's answer,
   * resolved at save time so the PDF can show the actual Q&A.
   */
  answeredQuestions?: Array<{ question: string; answer: string }>;
  /**
   * Present when the status is "passed" because an advisor overrode it rather
   * than because the client scored the bar. The PDF and the widget both need
   * to be able to tell those apart even though the status is the same.
   */
  override?: QuizOverride;
  /**
   * Present when the status came from an external attestation rather than from
   * an attempt here — same reasoning as `override`. `score` is null in that
   * case and `answeredQuestions` is empty: we have neither.
   */
  externalResult?: QuizExternalResult;
  /** QL-505 — who sat the governing attempt, as they stood then. Null for none, an external result, or (QL-660) while an advisor override is the standing. */
  answeredBy?: AnsweringPartyRecord | null;
  /** QL-505 — group aggregate only: members passed (incl. override/external) of the counted total. */
  memberSummary?: { passed: number; total: number };
};

/** Server-authored `calculatedData` blob of the `quiz` component value. */
export type QuizComponentResult = {
  /**
   * Keyed by quizId, matching `QuizStoredValue.quizStates`. For a group without
   * a representative this is the AGGREGATE (status "passed" only when every
   * counted member passed), so `readQuizResultStatus` and completion are unchanged.
   * The aggregate carries no score — score sources read `readQuizScoreRange` (QL-698).
   */
  results: Record<string, QuizOutcome>;
  /** QL-505 — absent on rows written before QL-505 = "single". */
  answeringMode?: AnsweringMode;
  /** QL-505 — "members" mode only: one entry per CURRENT member. */
  memberResults?: Array<{
    /** The subject's slot key (QL-654: a party or member id); `subjectId` repeats it and is absent on older rows. */
    memberId: string;
    subjectId?: string;
    name: string;
    results: Record<string, QuizOutcome>;
  }>;
  determinedAt: string;
};

/**
 * QL-698 — `GET /public/flow/quiz/condition-values`: every quiz's
 * `__quiz_score_<id>` value, resolved server-side from the full stored value,
 * so a member's session reads the whole group's score range. "min,max", "" for
 * no score. Same for every session.
 */
export type QuizConditionValuesResponse = { values: Record<string, string> };

/**
 * What the widget needs to render one quiz component: the score-stripped
 * definition plus this client's live standing on it.
 */
export type QuizForInstance = {
  quiz: QuizPublic;
  status: QuizResultStatus;
  availability: QuizAttemptAvailability;
  /** Set when status is "passed". */
  passedAt: string | null;
  /**
   * Set when the pass came from an advisor override rather than from a score.
   * The widget names who cleared the gate, the same way the PDF does.
   */
  override: QuizOverride | null;
  /**
   * Set when the standing comes from a result another system attested rather
   * than from an attempt sat here. Null the moment the client sits it in the
   * flow, because the in-flow result then governs.
   */
  externalResult: QuizExternalResult | null;
  /**
   * QL-505 — a group without a representative: one standing per current
   * member (the top-level fields are then the aggregate and `availability` is
   * not actionable). Absent for every other client.
   */
  members?: Array<{
    /** The subject's id (a `client_party.id` or `client_group_member.id`, QL-654); `subjectId` repeats it. */
    memberId: string;
    subjectId: string;
    /**
     * QL-653 — the `client_group_member.id` this line counts towards, so the lines of a
     * member with 2+ representatives group under their member (see
     * `ProductAssessmentMemberView.groupMemberId`). Null for a client-level representative.
     */
    groupMemberId?: string | null;
    name: string;
    /** QL-660 — who answers for this member right now (their own POA/guardian); null when the member answers themself. QL-653 — on a representative's own line (member with 2+ representatives) that representative, `partyId === subjectId`. */
    memberRepresentative: MemberRepresentative | null;
    /**
     * QL-660 — who sat the attempt that COUNTS for this member (frozen record), so the
     * line can name the real answerer: the POA, or the member themself. Null when
     * nothing counts yet, or the standing is an override/external result.
     */
    answeredBy?: AnsweringPartyRecord | null;
    status: QuizResultStatus;
    availability: QuizAttemptAvailability;
    passedAt: string | null;
    override: QuizOverride | null;
    externalResult: QuizExternalResult | null;
  }>;
};

/** QL-528 — one quiz attempt as the public API reports it: the stored attempt plus who sat it. Never part of `QuizForInstance`, which the client's own flow session reads. */
export type QuizAttemptWithParty = QuizAttempt & {
  /** Who sat this attempt, as they stood then (no national ID). Null while in flight, and for an attempt graded before QL-505. */
  answeredBy: AnsweringPartyRecord | null;
};

/** QL-528 — one answer of a graded standalone-quiz attempt, scored against the quiz as it was then. */
export type QuizAnswerScore = {
  questionId: string;
  /** Empty string for a drawn question the submission left unanswered. */
  selectedOptionId: string;
  /** The question text, frozen at grading. Plain string in the flow's language (a quiz is authored inside its flow, not translated). Null when the question was no longer configured. */
  questionLabel: string | null;
  /** The chosen option's text, same rule. Null when unanswered, or the option was gone. */
  selectedOptionLabel: string | null;
  /** What the selected option contributed. 0 when unanswered; null when the question or option was gone. */
  score: number | null;
  /** The best score any option of the question gives. Null when the question is gone or has no options. */
  maxScore: number | null;
};

/**
 * QL-528 — the score behind each answer of one COMPLETED standalone-quiz
 * attempt. Keyed by `quizId` (the flow component id); `attemptNumber` and
 * `answeredAt` are always set. Lives ONLY in `ComponentValueServerData.quiz.answerScores`:
 * next to a quiz that can be retaken, an option's score is the answer key.
 * Recorded in the transaction that grades the attempt; a later edit to the
 * quiz cannot rewrite it, and (while `recordedAt` is set) the answers add up to the attempt's `score`.
 */
export type QuizAnswerScores = {
  /** The subject this attempt is filed under (QL-654: a representative's `client_party.id` or a member's `client_group_member.id`). Null in single mode. Name kept from QL-505. */
  memberId: string | null;
  /** Repeats `memberId`. */
  subjectId: string | null;
  /** Who answered, as they stood then. Null for a legacy attempt with no answering-party event. */
  answeredBy: AnsweringPartyRecord | null;
  quizId: string;
  /** The attempt's own number — numbering restarts after a cooldown reset, so match on `answeredAt` too. */
  attemptNumber: number;
  /** The attempt's `completedAt`. */
  answeredAt: string;
  /** Null = derived from the CURRENT configuration on this read (attempt graded before QL-528); can still change. */
  recordedAt: string | null;
  answers: QuizAnswerScore[];
};

/** QL-528 — one subject's attempt history on one quiz. */
export type QuizAttemptHistory = {
  /** Completed attempts of the party who answers now, in the current cooldown window (same set as status/availability). Can be lower than the completed `attempts`. */
  attemptsCount: number;
  lastAttemptAt: string | null;
  /** Every attempt in order, including one in flight and attempts another answering party sat (compare `answeredBy`). */
  attempts: QuizAttemptWithParty[];
  /** One entry per COMPLETED attempt, in order. */
  answerScores: QuizAnswerScores[];
};

/** One `members[]` line of `QuizForInstance`. */
export type QuizMemberStanding = NonNullable<QuizForInstance["members"]>[number];

/** QL-528 — `GET /api/v1/flow-instances/{id}/quiz`: the standing plus the history (the top level holds the history of attempts sat while the client answered as one party; per party under `members[]` when several answer; member-keyed history of an earlier mode is listed under `members[]` too). */
export type QuizStandingWithAttempts = Omit<QuizForInstance, "members"> &
  QuizAttemptHistory & { members?: Array<QuizMemberStanding & QuizAttemptHistory> };

/** QL-528 — one quiz line of `QuizMemberView`. */
export type QuizMemberQuizView = { quiz: QuizPublic } & Omit<
  QuizMemberStanding,
  "memberId" | "subjectId" | "name" | "memberRepresentative" | "answeredBy"
> &
  QuizAttemptHistory;

/** QL-528 — `GET /api/v1/flow-instances/{id}/quiz/members[/{memberId}]`: the per-party audit trail when several parties answer. */
export type QuizMemberView = {
  /** The subject's id (a `client_party.id` or `client_group_member.id`); `subjectId` repeats it. */
  memberId: string;
  subjectId: string;
  /** Current name; for a party who has left, the name recorded with their last answer (empty when none). */
  name: string;
  /** False = member removed / representative removed or expired; their attempts are kept but no longer count. */
  isCurrentMember: boolean;
  /**
   * QL-653 — the `client_group_member.id` this line counts towards (the member's id on a line of
   * a member with 2+ representatives); null for a client-level representative or a departed entry.
   */
  groupMemberId?: string | null;
  /** QL-660 — current member: who answers for them now (their own POA/guardian); a member who left: as recorded on their last answer, else null. */
  memberRepresentative: MemberRepresentative | null;
  /** One entry per quiz the flow asks for, in the order the client meets them. */
  quizzes: QuizMemberQuizView[];
};

export type QuizMembersResponse = { members: QuizMemberView[] };

export type StartQuizRequest = {
  /**
   * QL-654 — whose answer this is, when several parties answer for the client
   * (a Group with no representative, or a client with 2+ representatives):
   * the `client_party.id` of a representative or the `client_group_member.id`
   * of a member. Required then, rejected (400) for every other client.
   * Preferred over `memberId`, which stays as an alias for a group member.
   */
  subjectId?: string;
  /** QL-505 — alias of `subjectId` for a group member. */
  memberId?: string;
  quizId: string;
};

/** Drawn questions with option scores stripped. */
export type StartQuizResponse = {
  attemptNumber: number;
  questions: QuizPublicQuestion[];
};

export type SubmitQuizRequest = {
  /**
   * QL-654 — whose answer this is, when several parties answer for the client
   * (a Group with no representative, or a client with 2+ representatives):
   * the `client_party.id` of a representative or the `client_group_member.id`
   * of a member. Required then, rejected (400) for every other client.
   * Preferred over `memberId`, which stays as an alias for a group member.
   */
  subjectId?: string;
  /** QL-505 — alias of `subjectId` for a group member. */
  memberId?: string;
  quizId: string;
  answers: Array<{ questionId: string; selectedOptionId: string }>;
};

export type SubmitQuizResponse = {
  passed: boolean;
  availability: QuizAttemptAvailability;
};

/**
 * Advisor-only. Never exposed on the public flow — a client cannot clear their
 * own gate.
 */
export type OverrideQuizRequest = {
  /**
   * QL-654 — whose answer this is, when several parties answer for the client
   * (a Group with no representative, or a client with 2+ representatives):
   * the `client_party.id` of a representative or the `client_group_member.id`
   * of a member. Required then, rejected (400) for every other client.
   * Preferred over `memberId`, which stays as an alias for a group member.
   */
  subjectId?: string;
  /** QL-505 — alias of `subjectId` for a group member. */
  memberId?: string;
  quizId: string;
  reason: string;
};

export type OverrideQuizResponse = {
  override: QuizOverride;
};

// --- Trapets Lookup Types ---

export type TrapetsLookupInput = {
  clientType: "Person" | "Company" | "Group";
  name: string;
  country: string;
  ssn?: string;
  ssnWithPrefix?: string;
  birthDate?: string;
  orgId?: string;
  iso3Country?: string;
  /**
   * QL-21 follow-up — the natural person screened on a Company/Group's behalf.
   *
   * Supplied by the form rather than resolved server-side: the advisor fills in
   * both halves of a company screening in one place. On save these are written
   * back to the client's Related parties, so "who acts for the client" and "who
   * was screened" cannot drift — which is what the flow-completion gate checks.
   */
  representativeName?: string;
  representativeSsn?: string;
  /** QL-c — the representative's DOB, screened when they have no national id. */
  representativeBirthDate?: string;
  /**
   * QL-62 — the representative's OWN jurisdiction, which is not always the
   * client's: a Swedish representative can act for a Norwegian company, and the
   * national id has to be searched under their country, not the company's.
   * Falls back to the client's country when unset.
   */
  representativeCountry?: string;
  /**
   * QL-654 — EVERY representative to screen, in order (a client can have
   * several: two signatories acting jointly, a couple under mutual POA). Takes
   * precedence over the single `representative*` fields above, which stay as the
   * one-representative shorthand. The lookup is only complete when each of them
   * was screened.
   */
  representatives?: TrapetsRepresentativeInput[];
};

/** QL-654 — one representative to screen: a name and a national ID or a date of birth. */
export type TrapetsRepresentativeInput = {
  name: string;
  ssn?: string;
  /** Screened when the representative has no national id. */
  birthDate?: string;
  /** The representative's OWN jurisdiction; falls back to the client's. */
  country?: string;
  /** The `client_party.id` / flagged member id this person is registered under, when they are. */
  partyId?: string;
};

export type TrapetsLookupResult = {
  hasBeenChecked: boolean;
  searchedAt: string;
  /** The CLIENT'S OWN search: the person for a Person, the entity by org
   *  number for a Company. Empty for a Group, which has no identity of its
   *  own to search. */
  rawResults: import("./trapets").TrapetsSearchResult[];
  status: import("./trapets").TrapetsStatus;
  isTestMode?: boolean;
  /**
   * QL-21 follow-up — the representative's person search, for a Company or
   * Group. PEP is a property of a natural person and Trapets reports none for
   * a company (docs/TRAPETS.MD), so without this the PEP question is simply
   * unanswered — which is what `subject.kind === "none"` records.
   */
  representativeStatus?: import("./trapets").TrapetsStatus | null;
  representativeRawResults?: import("./trapets").TrapetsSearchResult[];
  /**
   * Who the PEP half of this lookup is about. With several representatives
   * (QL-654) this is the FIRST one and `subjects` lists them all — readers that
   * need every person (completion, PDF, AI) read `subjects`.
   */
  subject?: import("./compliance-review").ScreeningSubject;
  /**
   * QL-654 — every person screened for PEP, in order. Absent on lookups saved
   * before QL-654 (read `subject`). The completion gate requires each current
   * representative's national id to appear here as a `representative`.
   */
  subjects?: import("./compliance-review").ScreeningSubject[];
  /**
   * QL-654 — each representative's own search, in the same order as `subjects`.
   * `representativeStatus` / `representativeRawResults` above are the UNION over
   * all of them (any PEP, any sanction, every match), so single-status readers
   * stay correct.
   */
  representatives?: TrapetsRepresentativeResult[];
};

/** QL-654 — one representative's search inside a `TrapetsLookupResult`. */
export type TrapetsRepresentativeResult = {
  subject: import("./compliance-review").ScreeningSubject;
  status: import("./trapets").TrapetsStatus;
  rawResults: import("./trapets").TrapetsSearchResult[];
};

// --- Enin Lookup Types ---

export type EninLookupInput = {
  /** Explicit country-prefixed Enin id (from the name-search picker) — wins when set. */
  companyId?: string;
  /** Otherwise the id is resolved via resolveEninCompanyId(orgId, country). */
  orgId?: string;
  country?: string;
  /** Display/audit only. */
  companyName?: string;
};

/** Curated Enin bundle — keys are real enin-service endpoint names. */
export type EninLookupBundle = {
  company_details: unknown | null;
  accounting_highlights: unknown | null;
  share_ownership_ultimate: unknown | null;
  company_flags: unknown | null;
  person_company_role: unknown | null;
};

export type EninLookupResult = {
  hasBeenChecked: boolean;
  searchedAt: string;
  companyId: string;
  companyName?: string;
  bundle: EninLookupBundle;
  status: import("./enin").EninStatus;
  /** QP-184 — AML screening of UBOs / key roles / representative. */
  personScreening?: EninPersonScreening | null;
};

/** One screened related person (UBO, senior role, or the representative). */
export type EninScreenedPerson = {
  name: string;
  kind: "ubo" | "role" | "representative";
  /** Display detail: stake for UBOs, role title for roles. */
  detail: string | null;
  isPEP: boolean;
  isSanctioned: boolean;
};

export type EninPersonScreening = {
  screenedAt: string;
  isTestMode: boolean;
  persons: EninScreenedPerson[];
};

// --- Risk Assessment Types ---

export type RiskAssessmentInput = {
  timeHorizonId?: string;
  riskLevelId?: string;
  /**
   * QP-180 — the client's answers to the model's adjustment questions, keyed
   * by questionId → answerId. Drives the config-driven score adjustment.
   * Present on both advisor and public surfaces (question-driven, not an
   * advisor override).
   */
  adjustmentAnswers?: Record<string, string>;
};

/**
 * Submit payload. The raw score is resolved SERVER-SIDE from the risk model's
 * matrix cell for the submitted (timeHorizonId, riskLevelId) pair — the matrix
 * is the authority, so no caller may set it. The adjustment is likewise derived
 * from `adjustmentAnswers` against the model's adjustment-question matrix;
 * there is no free-form advisor override.
 */
export type RiskAssessmentSubmitInput = RiskAssessmentInput & {
  /**
   * @deprecated Ignored by the server, which derives the score from the matrix.
   * Retained only so existing callers keep compiling; do not send it in new code.
   */
  score?: number;
};

export type RiskAssessmentResult = {
  score: number;
  timeHorizonName: string;
  riskLevelName: string;
  /**
   * The model portfolio the score matched — or, when nothing matched (QL-72),
   * the hand pick that fills the gap. Readers get it as it stands now, not as
   * it was when the score was written.
   */
  selectedPortfolioId?: string | null;
  /**
   * QL-72 — the model portfolio the score and platform match; null when none
   * does (a gap-filling hand pick is not counted). Absent on results stored
   * before it existed — `selectedPortfolioId` then held the match.
   */
  calculatedPortfolioId?: string | null;
  timestamp: string;
  isPartial?: boolean;
  // QP-180 — present only when the adjustment sum is non-zero or the score was
  // capped. QL-8: scoreAdjustment is the RAW decimal sum; truncate for display.
  scoreRaw?: number;
  scoreAdjustment?: number;
  isCapped?: boolean;
  /**
   * QL-212 — the cap value applied, written with the QP-180 fields on the
   * CLIENT-level result (an all-global model), so the client's own record can
   * print the cap row without borrowing a goal's `riskScoreCap` column.
   */
  scoreCap?: number | null;
  /**
   * QL-198 — set when the score resolved but NO model portfolio could be matched,
   * saying which of the three causes it was: `no_risk_score`,
   * `no_portfolio_for_score`, or `no_portfolio_for_platform`. Absent when a
   * portfolio resolved, and (QL-72) once every model portfolio of the goal has a
   * selection — a hand pick fills the gap, as flow completion counts it. Mirrors `DynamicGenerationMeta.errorReason` on the
   * dynamic path; without it a caller sees `selectedPortfolioId: null` and cannot
   * tell a misconfigured collection from an unfinished flow.
   */
  modelResolutionFailure?: "no_risk_score" | "no_portfolio_for_score" | "no_portfolio_for_platform";
};

/**
 * QL-212 — what applying a global answer did to one goal (D10).
 *
 * The fan-out is best effort: the client's answer is always stored, and each
 * goal is reported independently. `pending` means the goal's own inputs are not
 * finished yet (a mixed-scope model whose goal page is unanswered); `failed`
 * means the inputs ARE complete but could not produce a score, which is a
 * configuration problem the advisor has to fix. A gap on one goal never stops
 * the others being scored.
 */
export type RiskApplyFailureReason =
  /** The model's matrix has no cell for this goal's horizon × level pair. */
  | "matrix_gap"
  /** The flow has no risk model, so no score can be derived at all. */
  | "no_risk_model"
  /** An unexpected error while writing the goal — details are in the log. */
  | "apply_error";

export type RiskAssessmentGoalOutcome =
  | { goalId: string; status: "applied"; score: number }
  | { goalId: string; status: "pending"; reason: RiskInputsIncompleteReason }
  | { goalId: string; status: "failed"; reason: RiskApplyFailureReason };

/**
 * QL-212 — the instance-level (globally-scoped) risk-assessment submit payload,
 * the sibling of the goal-keyed `RiskAssessmentSubmitInput`. It carries no goal
 * id and no score: the score is derived per goal from the matrix, exactly as on
 * the goal-keyed path.
 */
export type SubmitInstanceRiskAssessmentRequest = RiskAssessmentInput;

export type SubmitInstanceRiskAssessmentResponse = {
  success: boolean;
  error?: string;
  /**
   * QL-212 — the rule a refusal hit (`risk_input_scope_mismatch`,
   * `risk_captured_per_goal`), for a caller that branches on causes rather than
   * on wording. Since QL-718 a refusal is an HTTP error body (`ApiError`), so a
   * 200 response never carries it; kept for the shape's other readers.
   */
  code?: string;
  /**
   * Set when the answer was NOT stored because the flow instance is no longer
   * editable. `success` stays true — a late autosave must not fail a caller —
   * so this is the only thing that distinguishes "ignored" from "stored and
   * fanned out", which otherwise look identical from the outside.
   */
  skipped?: "not_editable";
  inputData?: string;
  calculatedData?: string;
  /** Per-portfolio snapshot invalidations, merged across every goal applied. */
  refreshHints?: SnapshotInvalidation[];
  /** Dynamic-portfolio regeneration, merged across every goal applied. */
  dynamicSync?: DynamicRegenSync;
  /**
   * D10 — what happened to each goal. Present whenever the answer was stored.
   * Absent on a refusal — including `risk_matrix_gap`, returned BEFORE storing
   * when both ids are captured for the client and the model has no cell for
   * the pair (every goal would have failed on it).
   */
  goalOutcomes?: RiskAssessmentGoalOutcome[];
};

// --- Financial Situation Types ---

export type FinancialEntryItem = {
  id: string;
  typeId: string;
  /**
   * QL-330 — the field's sync key as the caller sent it. Kept on the stored
   * row so a read-merge-write round trip echoes it; `typeId` is always the
   * resolved field id.
   */
  typeSyncKey?: string;
  typeLabel: string;
  label: string;
  /**
   * QL-368 — absent while nothing has been entered yet (the widget seeds a
   * required field's row without one). `0` is a real value: it fills a
   * required field and is rendered as "0", not as an empty input.
   */
  amount?: number;
  monthlyPayment?: number;
  isin?: string;
};

export type FinancialSituationInput = {
  incomeEntries: FinancialEntryItem[];
  assetEntries: FinancialEntryItem[];
  liabilityEntries: FinancialEntryItem[];
  commitmentEntries: FinancialEntryItem[];
  incomeStability?: "permanent" | "temporary" | "variable";
  currency: string;
  /**
   * QP-176 — "are you expecting changes to your financial situation?".
   * Only meaningful when the model's expectingChangesQuestion is enabled;
   * the note is required (completion-wise) when the answer is true.
   */
  expectingChanges?: boolean;
  expectingChangesNote?: string;
  /**
   * QP-183 — investment capacity calculator inputs. Only meaningful when
   * the model's investmentCapacity variant for the client type is enabled.
   * The income/expenses/surplus triangle: the server re-derives the third
   * value authoritatively on every save (income + expenses win).
   */
  monthlyIncome?: number | null;
  monthlyExpenses?: number | null;
  monthlySurplus?: number | null;
  /**
   * QL-127 — the triangle inputs when the model's variant has
   * `inputFrequency: "annual"`: what was typed, kept as typed (storing a
   * monthly figure would round-trip through ÷12/×12). The monthly keys above
   * are ignored in that mode, and these are ignored in monthly mode.
   */
  annualIncome?: number | null;
  annualExpenses?: number | null;
  /** Advisor-entered liquidity buffer; pre-filled to the computed minimum. */
  liquidityBuffer?: number | null;
  /**
   * QL-127 — `minimum` uses the calculated minimum and ignores the entered
   * buffer (kept, so switching back restores it); `custom` uses the entered
   * buffer. Absent = the pre-QL-127 inference: custom iff a buffer was entered.
   */
  liquidityBufferMode?: LiquidityBufferMode;
  shortTermExpenses?: number | null;
};

/** QL-127 — see `FinancialSituationInput.liquidityBufferMode`. */
export const LIQUIDITY_BUFFER_MODES = ["minimum", "custom"] as const;
export type LiquidityBufferMode = (typeof LIQUIDITY_BUFFER_MODES)[number];

/**
 * QL-40 — one synced client account, converted into the flow instance
 * currency and snapshotted into the component value. Amounts are null when
 * no FX rate was available (`conversionFailed`); such rows are displayed in
 * their original currency and excluded from every total.
 */
export type FinancialSituationAccountEntry = {
  accountId: string;
  name: string;
  vendor: string | null;
  /** Cash portion in the flow currency. */
  liquidAmount: number | null;
  /** Positions portion in the flow currency. */
  investmentAmount: number | null;
  /** liquidAmount + investmentAmount. */
  amount: number | null;
  originalCurrency: string;
  /** Cash + positions total in the account's own currency. */
  originalAmount: number;
  conversionFailed?: boolean;
};

export type FinancialSituationResult = {
  /**
   * QL-39 — totals per category and per field. The former liquid / investment /
   * retirement / real-estate and secured / unsecured / contingent splits were
   * derived from the fields' `type`, which no longer exists.
   */
  totals: {
    totalAnnualIncome: number;
    incomeByType: Record<string, number>;
    totalAssets: number;
    assetsByType: Record<string, number>;
    totalLiabilities: number;
    totalMonthlyDebtPayments: number;
    liabilitiesByType: Record<string, number>;
    totalMonthlyCommitments: number;
    commitmentsByType: Record<string, number>;
  };
  /**
   * QL-39 — the type-neutral ratios. Liquid net worth, the emergency-buffer
   * ratio and the old capacity-for-loss figure needed the liquid-asset split
   * and went with it; the QP-183 `capacity` block is the capacity measure.
   */
  metrics: {
    netWorth: number;
    debtToIncomeRatio: number;
    monthlyDebtServiceRatio: number;
    savingsRate: number;
  };
  /**
   * QP-195 — derived statuses keyed by status def key (e.g. { segment: "affluent" }).
   * Present only when at least one status could be derived from the entered data.
   */
  statuses?: Record<string, string>;
  /**
   * QP-183 — server-computed investment capacity. Present only when the
   * model's investmentCapacity variant for the instance's client type is
   * enabled at save time.
   */
  capacity?: {
    monthlyIncome: number | null;
    monthlyExpenses: number | null;
    /** Authoritative surplus; null when fewer than two triangle inputs. */
    monthlySurplus: number | null;
    /**
     * max(bufferFloor × memberCount, basis × bufferMultiplier), rounded up to a
     * whole amount; the basis is monthly income or, QL-127, monthly expenses
     * per the variant — or the fixed buffer (used literally) when one applies.
     */
    bufferMinimum: number;
    /**
     * Effective buffer used in the calc: the fixed buffer when one applies;
     * else the minimum in `minimum` mode, or in `custom` mode the entered
     * amount clamped to >= minimum (the minimum when no amount was entered).
     */
    liquidityBuffer: number;
    /** True when the entered buffer is below the minimum (blocks completion). */
    bufferBelowMinimum: boolean;
    /**
     * QL-107 — present (and true) only when the model's fixed buffer was in
     * force at save time, so the stored block is self-describing for audit
     * without re-resolving the client's flag. Absent = the derived path (as on
     * every block stored before QL-107).
     */
    fixedBufferApplied?: boolean;
    /**
     * QL-127 — present (and true) when the side was taken from the financial
     * situation's own entries (income entries ÷ 12; commitments + liability
     * monthly payments) instead of the typed triangle. Absent = typed.
     */
    incomeFromEntries?: boolean;
    expensesFromEntries?: boolean;
    /**
     * QL-127 — present (and true) when the buffer mode is `custom` but no
     * amount was entered; the minimum stands in and completion is blocked
     * (`buffer_missing`).
     */
    liquidityBufferMissing?: boolean;
    /**
     * QL-127 — the variant's modes the block was computed under, so a caller
     * that cannot read the model (the AI tools, a public API partner) knows
     * which input keys apply, plus the buffer mode as resolved (an absent
     * input mode inferred). Absent on blocks stored before QL-127.
     */
    inputFrequency?: CapacityInputFrequency;
    bufferMultiplierBasis?: CapacityMultiplierBasis;
    liquidityBufferMode?: LiquidityBufferMode;
    shortTermExpenses: number;
    /** Sum of the configured asset fields' entry amounts. */
    investablePool: number;
    /** max(0, investablePool - liquidityBuffer - shortTermExpenses). */
    availableForInvestments: number;
  };
  /**
   * QL-40 — additive: present only when the client had synced accounts at
   * save time. Converted amounts are already folded into `totals.totalAssets`
   * and therefore into `metrics`.
   */
  accountEntries?: FinancialSituationAccountEntry[];
  /** Sum of the successfully converted account amounts, in the flow currency. */
  accountsTotal?: number;
  /** When the account snapshot inside this value was taken. */
  accountsSyncedAt?: string;
  completedAt: string;
  lastUpdatedAt: string;
};

// --- Sumsub Verification Types ---

export type SumsubVerificationInput = {
  externalUserId: string;
  applicantId: string;
  /**
   * QL-654 — whose identity this verification is about, when several people are
   * verified (a representative's `client_party.id`). Absent for a single person.
   */
  subjectId?: string | null;
};

export type SumsubVerificationResult = {
  hasBeenChecked: boolean;
  /**
   * Provenance: true when the verification was performed against the Sumsub
   * sandbox (test mode). Stamped at save time — from the matched webhook
   * environment on the webhook path, from the tenant's current mode on the
   * widget save path. Backs the test/production badge and mode-switch
   * invalidation.
   */
  isTestMode: boolean;
  searchedAt: string;
  status: {
    isVerified: boolean;
    reviewStatus: string;
    reviewAnswer: string | null;
  };
  info: {
    firstName?: string;
    lastName?: string;
    dob?: string;
    country?: string;
  } | null;
  documentExpiry: string | null;
  rawData: unknown;
  /**
   * QL-f — identity conflicts someone has explicitly dismissed.
   *
   * Kept beside the result they are about rather than in their own table: a
   * dismissal only applies while both compared values are unchanged, so one
   * carried across a re-save of the same result stays valid, and one that
   * outlives a NEW scan goes inert by itself because the document's value no
   * longer matches. Correcting the record is not recorded here at all — it
   * removes the conflict instead.
   */
  identityResolutions?: IdentityConflictResolution[];
  /**
   * QL-654 — one verification per person, keyed by subject id, present only when
   * the client has 2+ people to verify. The top-level fields above are then the
   * AGGREGATE over them (verified only when everyone is — see
   * `aggregateSumsubSubjects`), so single-result readers stay correct; a
   * dismissed identity conflict lives on the person's own entry.
   */
  subjects?: Record<string, import("./sumsub-subjects").SumsubSubjectVerification>;
};

export type SumsubVerificationPreviousAnswer = {
  inputData: string;
  calculatedData: string;
  timestamp: string | null;
} | null;

// --- Advisor Notes ---

export type AdvisorNoteResponse = {
  notes: string | null;
};

// --- Reference Data ---

export type ReferenceOption = {
  id: string;
  label: string;
  order: number;
};

export type AvailableProduct = {
  id: string;
  name: string;
  isin: string | null;
  productType: string | null;
};

export type ProductSuitabilityItem = {
  id: string;
  name: string;
  isin: string | null;
  productTypeId: string | null;
  productTypeName?: string;
  minTimeHorizonId: string | null;
  minTimeHorizonOrder?: number;
  minTimeHorizonName?: string;
  minRiskLevelId: string | null;
  minRiskLevelOrder?: number;
  minRiskLevelName?: string;
  /** QL-10 — minimum goal risk score required for suitability. Absent = not configured; 0 is a real score. */
  minRiskScore?: number;
};

export type ReferencePortfolio = {
  id: string;
  name: string;
  riskScore: number;
};

export type SuitabilityContext = {
  classification: unknown;
  experience: unknown;
  goalRiskLevelOrder?: number;
  goalRiskLevelName?: string;
  /** QL-10 — the goal's FINAL risk score (matrix + adjustment, after the compliance cap). Absent = no submitted risk assessment. */
  goalRiskScore?: number;
  goalTimeHorizonOrder?: number;
  goalTimeHorizonName?: string;
};

export type RiskModelOptions = {
  riskLevels: ReferenceOption[];
  timeHorizons: ReferenceOption[];
};

// --- Flow Completion Validation (engine-driven; see INCOMPLETE_FLOW_SUBMISSION_BACKEND_PLAN.md) ---

/** A single unfilled requirement on a flow instance. */
export type MissingRequirement = {
  /**
   * Page the missing thing lives on. Set for every kind EXCEPT
   * `"instance_snapshot"` (D38 placement-less snapshot — no flow-page anchor).
   */
  pageId?: string;
  /** Raw page.name — frontend localizes (D7). Set when `pageId` is set. */
  pageName?: string;
  /** Set for goal-scoped fields, goal_input, and components on goal pages. */
  goalId?: string;
  /**
   * Set for portfolio-scoped components on the portfolio page (QP-150): portfolio_input,
   * custom_portfolio, and the per-portfolio snapshot display components. The portfolio
   * page renders once per portfolio, so the gap is attributed to a specific portfolio
   * (drives the portfolio tab-strip error dot + `portfolioId:componentId` widget border).
   */
  portfolioId?: string;
  /**
   * Discriminator for rendering and remediation:
   * - `field`             — required field empty (user fills it in)
   * - `component`         — interactive component's predicate failed; user completes the widget.
   *                         Covers the 7 interactive widgets (classification, sumsub, trapets,
   *                         product_assessment, financial_situation, risk_assessment,
   *                         custom_portfolio) PLUS `goal_input` per D35 (one entry per goal when
   *                         name/scheduleType/amount-when-model are incomplete; replaces the
   *                         per-input `goal_name` + `goal_amount` kinds removed pre-merge).
   * - `snapshot`          — derived `flow_instance_snapshot` row is missing; consumer is a
   *                         placed display component (`portfolio_display`, `portfolio_xray`,
   *                         etc.). Remediation is regeneration, not user input. Per D18 + Phase 2.
   * - `instance_snapshot` — D38 — `flow_instance_snapshot` row is missing for an
   *                         **instance-wide snapshot that has no placed flow-page consumer**.
   *                         Currently fires only for `instance_expected_value` (consumed by the
   *                         wizard header's `hasWithdrawalExceedsValue` gate, not a renderable
   *                         widget). Carries only `snapshotType` — `pageId` / `pageName` /
   *                         `componentId` / `goalId` are all absent because there's no anchor.
   * - `advisor_note`      — QL-43 — the page's advisor-notes editor is enabled AND marked
   *                         required, and the stored note is empty (or whitespace only).
   *                         The only PAGE-level requirement the engine emits: it carries
   *                         `pageId` / `pageName` (plus `goalId` on a goal page, where the
   *                         editor renders once per goal) and no `componentId`, because the
   *                         editor is part of the page chrome rather than a placed component.
   * - `advisor_review`    — QL-314 — an ADVISOR-ONLY compliance component placed on an
   *                         internal page (volatility_chart, equity_share_chart) blocks
   *                         this goal, reported on
   *                         the PUBLIC workflow. Carries `goalId` + `reason` only: no
   *                         `pageId` / `pageName` (the component sits on no public page),
   *                         no `componentId` / `componentType` (the client must not learn
   *                         which component or what the numbers are). Remediation is the
   *                         advisor's decision; the public UI shows an "advisor action
   *                         required" alert per goal and at flow level.
   * - `config_error`      — QL-493 — the flow TEMPLATE cannot enforce a risk
   *                         bound its risk model sets (chart / assessment not
   *                         placed, or the collection cannot host the charts).
   *                         Flow-level: carries `reason` (a `ConfigErrorReason`)
   *                         and, when a component is what is missing,
   *                         `componentType`; no page / goal / component anchor.
   *                         Emitted on BOTH workflows. Remediation is a flow
   *                         designer's; the advisor sees a template alert, the
   *                         client the generic "advisor action required".
   * - `followup_conflict` — QL-576 — a follow-up whose relinked accounts would
   *                         share a wrapper type on one successor goal (bank data
   *                         drifted after creation — a wrapper filled, or an
   *                         account linked to the goal, after the follow-up was
   *                         created). Goal-level: carries `goalId`,
   *                         `reason: followup_account_wrapper_conflict`,
   *                         `wrapperType` (null = both without one) and the two
   *                         `accounts` (id + name). Emitted on BOTH workflows;
   *                         blocks submit because completion would refuse it.
   *                         Remediation: cancel and recreate the follow-up with
   *                         the accounts in separate goals.
   */
  kind:
    | "field"
    | "component"
    | "snapshot"
    | "instance_snapshot"
    | "advisor_note"
    | "advisor_review"
    | "config_error"
    | "followup_conflict";
  /** kind === "field". */
  fieldId?: string;
  fieldName?: string;
  /** kind === "component" or kind === "snapshot" (the consuming display component). */
  componentId?: string;
  componentType?: string;
  /** kind === "snapshot" or kind === "instance_snapshot" — `flow_instance_snapshot.snapshotType`. */
  snapshotType?: string;
  /** Set when the missing thing lives inside a `tabs_block` layout. Drives inner tab markers (H4). */
  layoutId?: string;
  tabId?: string;
  /** kind === "followup_conflict" — null = both accounts hold no wrapper type. */
  wrapperType?: string | null;
  /** kind === "followup_conflict" — the two accounts that would collide. */
  accounts?: FollowupConflictAccount[];
  /**
   * QP-176/QP-183 — machine-readable failure reason so widgets can render a
   * specific message. Emitted for:
   *
   * - `financial_situation`: `baseline` | `required_fields_missing` (QL-39) |
   *   `assets_required` | `liabilities_required` | `expecting_changes_unanswered` |
   *   `expecting_changes_note_missing` | `capacity_missing` |
   *   `buffer_missing` (QL-127) | `buffer_below_minimum` | `deposits_exceed_capacity` |
   *   `recurring_deposits_exceed_surplus`.
   * - `custom_portfolio` (QL-138): `no_products` | `min_buy_violation` |
   *   `account_below_minimum` (QL-362) |
   *   `switch_over_allocated` | `switch_regime_conflict` |
   *   `switch_wrapper_incompatible` (QL-283). (The `risk_bound_*` family was
   *   removed by QL-493: risk bounds are gated per goal by the volatility and
   *   equity share charts, with their own reasons.)
   * - `portfolio_input` (QL-198): `no_model_portfolio_for_score` |
   *   `no_model_portfolio_for_score_on_platform` — the goal has a score but no
   *   active model portfolio matches it; `account_below_minimum` (QL-362) — a
   *   model or dynamic portfolio feeds an account below its wrapper type's minimum;
   *   `dynamic_unsuitable_product` (QL-584) — a dynamic portfolio holds a base
   *   product the client is not eligible for and nothing can replace it;
   *   `dynamic_sustainability_not_matched` (QL-657) — a dynamic portfolio
   *   cannot match the client's sustainability preferences.
   * - `risk_assessment`, global-page slot only (QL-212): `risk_not_scored` —
   *   the client-level answer is recorded but a goal that needs a score has
   *   none (the fan-out is best effort: matrix gap, apply error, a goal created
   *   while the model had no cell). Under an all-global model the goal page
   *   renders nothing, so this entry is the only place the gap can surface.
   * - `volatility_chart` (QL-314): `volatility_bound_breach` (breach, no
   *   decision yet) | `volatility_bound_not_accepted` (advisor chose "do not
   *   proceed"). A goal whose volatility cannot be evaluated is soft-skipped
   *   and emits nothing. On the public workflow these arrive as
   *   `kind: "advisor_review"` entries — goalId + reason only, no
   *   `componentId` / `componentType` / `pageId` — so the client sees only
   *   that advisor action is required, not which component or what the
   *   numbers are.
   * - `equity_share_chart` (QL-313): `equity_share_bound_breach` |
   *   `equity_share_bound_not_accepted` — the same shape for the equity share
   *   measure against `maxEquitySharePct`.
   */
  reason?: MissingRequirementReason;
};

/** QP-176/QP-183 — machine-readable failure reasons for `financial_situation`, in check order (first failure wins). */
export type FsIncompleteReason =
  | "baseline"
  | "required_fields_missing"
  | "assets_required"
  | "liabilities_required"
  | "expecting_changes_unanswered"
  | "expecting_changes_note_missing"
  | "capacity_missing"
  /** QL-127 — buffer mode is custom but no amount was entered. */
  | "buffer_missing"
  | "buffer_below_minimum"
  | "deposits_exceed_capacity"
  | "recurring_deposits_exceed_surplus";

/**
 * QL-138 — machine-readable failure reasons for `custom_portfolio`, in the
 * order the gates are checked (first failure wins). Risk bounds are not among
 * them since QL-493: the goal-level charts carry their own reasons.
 */
export type CustomPortfolioIncompleteReason =
  | "no_products"
  | "min_buy_violation"
  /** QL-362 — the account this portfolio's one-time buys would land in holds
   * less than its wrapper type's minimum. An aggregate: every portfolio feeding
   * that account carries the reason, so the fix may live on a sibling. */
  | "account_below_minimum"
  /** QL-283 — switch pairs on one holding exceed 100 % / its frozen units. */
  | "switch_over_allocated"
  /** QL-283 — a paired holding also carries a delta/sell instruction. */
  | "switch_regime_conflict"
  /** QL-283 decision 19 — a pair's target can no longer be held by the
   * source account: the account's wrapper was declared (or the target's
   * preferences edited) AFTER the pair was created. A switch executes
   * inside the source account, so the only exit is dropping the pair
   * (cross-wrapper intent = sell + new-money buy). */
  | "switch_wrapper_incompatible";

/**
 * QL-198 — machine-readable failure reasons for `portfolio_input`, emitted when
 * a MODEL portfolio has a risk score but no resolvable model portfolio.
 *
 * `resolveModelPortfolio` (QP-226) is platform-aware with explicit membership:
 * when the goal has a platform, only active collection portfolios tagged to it
 * resolve — there is no fallback to untagged portfolios. A platform with no
 * tagged model portfolios therefore silently produced a permanently
 * unsubmittable flow; these reasons make the gate say why.
 */
export type PortfolioInputIncompleteReason =
  /**
   * QL-664 — a model or dynamic portfolio on a goal that a mandate template
   * governs (one resolves for its platform and risk score): custom only.
   * The portfolio stays; submit waits until it is switched to custom.
   */
  | "portfolio_type_restricted_by_mandate"
  /** No active model portfolio matches the goal's risk score (collection without platforms, or goal without one). */
  | "no_model_portfolio_for_score"
  /** The goal has a platform, and no active model portfolio tagged to it matches the risk score. */
  | "no_model_portfolio_for_score_on_platform"
  /** QL-362 — a MODEL portfolio's one-time buys land in an account that would
   * hold less than its wrapper type's minimum. An aggregate: every portfolio
   * feeding that account carries the reason, so the fix may live on a sibling. */
  | "account_below_minimum"
  /** QL-584 — a DYNAMIC portfolio holds a position flagged
   * `unsuitableNoReplacement`. Always blocks; the position's own note names
   * the product on the advisor surfaces. */
  | "dynamic_unsuitable_product"
  /** QL-657 — a DYNAMIC portfolio cannot match the client's sustainability
   * preferences (generation `sustainability_not_matched`, or a pre-QL-657
   * generation still holding a product that fails them). Always blocks. */
  | "dynamic_sustainability_not_matched"
  /** QL-556 — a RECURRING portfolio's explicit end month falls before its
   * start month (the explicit one, or the current month an unset start
   * defaults to when the orders are built). */
  | "recurring_end_before_start";

/**
 * QL-212 — the reason a global `risk_assessment` slot can be open while its
 * inputs are answered: the client-level answer was recorded, but the goals it
 * should have scored carry no score. Under an all-global model this is the only
 * place the gap can be reported — the goal-page placement renders nothing and is
 * dropped from the traversal — so without it the flow would submit with unscored
 * goals.
 */
export type RiskAssessmentIncompleteReason = "risk_not_scored";

/**
 * QL-305 — the union of every machine-readable failure reason a
 * `MissingRequirement` can carry, so widgets that switch on the literals
 * (financial situation banner, custom-portfolio error line, portfolio-input
 * gate, risk-assessment not-scored message) are checked against the engine's
 * vocabulary at compile time instead of silently falling back to a generic
 * message after a rename.
 */
/**
 * QL-314 — volatility chart failure reasons (goal-scoped). QL-493 added the
 * fail-closed pair: the ceiling applies but the goal could not be evaluated
 * (no risk score, or no usable forecast / a portfolio dropped from the merge).
 */
export type VolatilityChartIncompleteReason =
  | "volatility_bound_breach"
  | "volatility_bound_not_accepted"
  | "volatility_no_risk_score"
  | "volatility_not_evaluable";

/** QL-313 — equity share chart failure reasons (goal-scoped); same pair as above. */
export type EquityShareChartIncompleteReason =
  | "equity_share_bound_breach"
  | "equity_share_bound_not_accepted"
  | "equity_share_no_risk_score"
  | "equity_share_not_evaluable";

/**
 * QL-375 — mandate check failure reasons (goal-scoped). The four of the
 * compliance-chart shape plus `mandate_required_missing`: the goal type says a
 * mandate is required and none resolved — not a breach, not missing data.
 */
export type MandateIncompleteReason =
  | "mandate_breach"
  | "mandate_not_accepted"
  | "mandate_no_risk_score"
  | "mandate_not_evaluable"
  | "mandate_required_missing";

/**
 * QL-493 — the flow TEMPLATE cannot enforce a ceiling the risk model sets:
 * the enforcing component is not placed, or the collection cannot host it.
 * Carried by `kind: "config_error"` entries; only a flow designer can resolve
 * them. Flow-level: no page, goal or component anchor.
 */
export type ConfigErrorReason =
  | "volatility_chart_not_placed"
  | "equity_share_chart_not_placed"
  | "risk_assessment_not_placed"
  | "risk_bounds_collection_setting_off"
  // QL-375 — the collection has mandates enabled but the flow places no
  // `mandate` component on a goal page (or on a portfolio page under
  // portfolio-level blocking).
  | "mandate_not_placed";

/** QL-576 — the one reason a `followup_conflict` entry carries. */
export type FollowupConflictReason = "followup_account_wrapper_conflict";

export type MissingRequirementReason =
  | FsIncompleteReason
  | CustomPortfolioIncompleteReason
  | PortfolioInputIncompleteReason
  | RiskAssessmentIncompleteReason
  | VolatilityChartIncompleteReason
  | EquityShareChartIncompleteReason
  | MandateIncompleteReason
  | ConfigErrorReason
  | FollowupConflictReason;

/** QL-576 — one account named for the advisor alert / public-API payload. */
export type FollowupConflictAccount = { id: string; name: string };

/**
 * QL-576 — a wrapper collision the completion validation found (same rule the
 * completion assert runs): both accounts would hold the same wrapper type on
 * one successor goal. Produced by `findFollowupWrapperConflicts`, stored on
 * `ComponentCompletionData.followupWrapperConflicts`, and emitted as one
 * `MissingRequirement` of `kind: "followup_conflict"` per entry.
 */
export type FollowupWrapperConflict = {
  goalId: string;
  wrapperType: string | null;
  accounts: [FollowupConflictAccount, FollowupConflictAccount];
};

/** The flow-instance completion picture: progress, denominator, and what's missing. */
/**
 * One required item and where it stands. `missing` lists only the open ones;
 * this lists every slot the traversal counted, so a caller can render the whole
 * checklist rather than just the gaps.
 *
 * Three states, not two: `not_applicable` is a slot that IS in `totalRequired`
 * but is not actionable yet because a prerequisite has not landed (D34) — e.g. a
 * snapshot whose inputs do not exist. Reporting those as "open" would tell a
 * client to go and do something they cannot do.
 */
export type RequirementStep = MissingRequirement & {
  status: "complete" | "open" | "not_applicable";
};

/**
 * QL-676 — the completion picture without the per-item `steps` list. Served by
 * GET /internal/flow-instances/:id/completion and GET /public/flow/instance/completion:
 * the wizards refetch it after almost every edit and never read `steps`, which
 * runs to hundreds of KB on a large multi-goal instance.
 */
export type FlowCompletionSummary = {
  /** 0-100. */
  percent: number;
  totalRequired: number;
  filledRequired: number;
  /** Every unfilled requirement in traversal order (page -> goal -> layout). */
  missing: MissingRequirement[];
  /** ISO 8601 — when the underlying flow definition was last edited. Stale-cache hint. */
  definitionUpdatedAt: string;
};

export type FlowRequirements = FlowCompletionSummary & {
  /**
   * Every counted requirement in the same traversal order, each with its state.
   * `missing` is the `status === "open"` subset. Served by the public API
   * `/status`, the AI completion tools and the 422 gate body. The web wizards
   * read `FlowCompletionSummary` and never see it (QL-676).
   */
  steps: RequirementStep[];
};

/**
 * 422 body returned by POST /complete when the gate rejects. The `error` field carries the
 * human-readable message (matches the codebase's existing error envelope convention — see
 * `apps/api/src/middleware/error.ts`); discriminate from other 422s via `requirements` presence.
 */
export type CompleteInstanceErrorResponse = {
  error: string;
  requirements: FlowRequirements;
};

// ─── Existing-portfolio snapshot (followup comparison) ──────────────────────
// GET /internal/flow-instances/portfolios/:portfolioId/existing-snapshot AND
// GET /public/flow/portfolios/:portfolioId/existing-snapshot — same shape.

/**
 * One frozen holding from the source account. `productId: null` marks an
 * unmatchable holding (ISIN missing or unknown to the collection, QL-203) —
 * it exists only on the existing side of the comparison and is identified by
 * `name` when `isin` is null.
 */
export type ExistingSnapshotPosition = {
  id: string;
  productId: string | null;
  isin: string | null;
  name: string | null;
  productName: LocalizedString | null;
  amount: number;
  units: number | null;
  order: number;
  /**
   * QL-304 — the advisor's sell decision for an unmatchable holding: null =
   * keep (the default), a value = sell that much on completion (capped at
   * `amount`; = `amount` is a full sell). Only meaningful when `productId`
   * is null; always null on matched rows.
   */
  sellAmount: number | null;
  /**
   * Share of the full advisable portfolio (matched custom rows + unsold
   * unmatchable value) held by this holding's unsold remainder, in %. Set
   * only on unmatchable rows (`productId: null`) of an account followup —
   * matched rows carry their weight on the custom-product row, and agreement
   * lines have no weight. 0 when the holding is fully sold.
   */
  weight: number | null;
};

export type ExistingSnapshotResponse = {
  positions: ExistingSnapshotPosition[];
  /**
   * QL-502 review #2 — which table `positions` came from, so a consumer can
   * tell a true frozen baseline (`existing_portfolio_snapshot`, survives the
   * source account being deleted) from the agreement follow-up's savings-plan
   * fallback (never a frozen baseline, server caps it at 0 regardless).
   */
  source: "existing_portfolio_snapshot" | "savings_plan";
};

// ─── Cost/benefit snapshot (QL-416) ─────────────────────────────────────────
// GET /internal/flow-instances/portfolios/:portfolioId/snapshot/cost-benefit AND
// GET /public/flow/portfolios/:portfolioId/snapshot/cost-benefit — same shape
// (wrapped in GoalSnapshotResponse<CostBenefitSnapshot>).

/**
 * One side of the comparison table — all figures are annual percentages. A
 * figure is null when its input is missing (no allocation yet, an asset class
 * without statistics, no mapped holding at all); the widget renders a dash.
 *
 * Basis: return and volatility are the risk-model blend over the side's asset
 * allocation RESCALED to its classified share (the unclassified remainder —
 * unmapped holdings, products without allocations — is excluded rather than
 * counted as zero), on both sides alike. The Forecast widget blends the raw
 * allocation, so the two agree only when the proposal is fully classified.
 */
export type CostBenefitSide = {
  /** Annual expected return %, blended over the classified share. */
  expectedReturnPct: number | null;
  /** Annual expected volatility %, blended over the classified share. */
  volatilityPct: number | null;
  /**
   * Weighted average of the products' RECURRING cost values (TER-like), in %.
   * Product-level only — advisor/platform fees are not part of it.
   */
  productCostPct: number | null;
};

/**
 * An existing holding that could not be mapped to a collection product
 * (`existing_portfolio_snapshot.productId` null — QL-304 principles). Kept at its
 * true weight in the composition but excluded from the metrics and cost figures;
 * the component lists these in a warning and reports the covered share.
 */
export type CostBenefitUnmatchedHolding = {
  name: string;
  isin: string | null;
  amount: number;
  /** Share of the existing portfolio's total, in %. */
  weightPct: number;
};

/**
 * The frozen existing side. Written once at followup creation as the
 * `existing_cost_benefit` snapshot and copied into every `cost_benefit`
 * regeneration so the widget needs one read.
 */
export type CostBenefitExistingSide = CostBenefitSide & {
  /**
   * Share (%) of the existing portfolio mapped to a collection product — the
   * basis of `productCostPct`.
   */
  coveragePct: number;
  /**
   * Share (%) of the existing portfolio with an asset-class allocation — the
   * basis of the return/volatility blend. Never above `coveragePct`. < 100
   * triggers the "only X % analysed" disclaimer.
   */
  classifiedPct: number;
  unmatched: CostBenefitUnmatchedHolding[];
};

/**
 * One-time costs of moving from the existing composition to the proposed one,
 * in the flow currency. Legs are the per-product amount differences between the
 * frozen `existing_portfolio` composition and the switch-aware `portfolio`
 * composition (plus the advisor's sell decisions on unmatched holdings).
 */
export type CostBenefitOneTimeCosts = {
  /** WITHDRAWAL-category fees on the sell legs. */
  withdrawal: number;
  /** DEPOSIT-category fees on the buy legs. */
  deposit: number;
  /** Collection buy/sell FX fees on legs whose instrument currency differs from the flow currency. */
  currency: number;
  total: number;
};

export type CostBenefitSnapshot = {
  currency: string;
  proposed: CostBenefitSide;
  /** null = the portfolio is not a followup (no existing portfolio to compare with). */
  existing: CostBenefitExistingSide | null;
  /** null = not a followup. */
  oneTimeCosts: CostBenefitOneTimeCosts | null;
  timestamp: string;
};

/**
 * QL-309 — what a follow-up portfolio is linked to, on every portfolio read
 * (null for a manually created portfolio). `name` / `externalId` label the
 * source (null when the source row is no longer readable);
 * `lockedScheduleType` is the schedule the follow-up is locked to — account
 * → one_time, savings agreement → recurring — and a change away from it is
 * rejected server-side (`schedule_type_locked`).
 */
/** What a follow-up portfolio follows up — the public name for each participant kind. */
export const FOLLOWUP_SOURCE_KINDS = ["account", "savingsPlan"] as const;
export type FollowupSourceKind = (typeof FOLLOWUP_SOURCE_KINDS)[number];

export type PortfolioFollowupSource = {
  kind: FollowupSourceKind;
  id: string;
  name: string | null;
  externalId: string | null;
  lockedScheduleType: "one_time" | "recurring";
};

/**
 * The server-computed fields every goal-portfolio read carries on top of the
 * stored row — `GET …/goals/:goalId/portfolios` and `GET …/portfolios/:portfolioId`
 * on the internal AND public flow surfaces.
 */
export type GoalPortfolioComputedFields = {
  /** Wrapper type of the follow-up's source account (null off a follow-up). */
  sourceAccountWrapperType: string | null;
  followupSource: PortfolioFollowupSource | null;
  /** QP-193 — purchases below their product's minimum buy; empty unless the
   * collection enforces minimums. QL-398 — on an account follow-up one entry
   * per transaction (top-up, new buy, or one switch pair), never the kept
   * holding. */
  minBuyViolations: MinBuyViolation[];
  /** QL-362 — accounts this portfolio's one-time buys would leave below their
   * wrapper type's minimum. A goal-level aggregate: sibling portfolios feeding
   * the same account carry the same entry. */
  accountMinimumViolations: AccountMinimumViolation[];
  /** QL-584 — a generated position is flagged `unsuitableNoReplacement`. */
  hasUnsuitableGeneratedPosition: boolean;
  /** QL-663 — what one event invests (the one-time buy or one recurring
   * installment). Custom portfolios sum their product rows; an account
   * follow-up reports the net new cash against its holdings. Null until an
   * amount is entered. */
  amountPerEvent: number | null;
  /** QL-72 — the model portfolio the goal's risk score and platform match
   * (null: none matches, or not a model portfolio). */
  calculatedPortfolioId: string | null;
  /** QL-72 — the model portfolio chosen by hand; null = Automatic (always null
   * off model portfolios). The portfolio used is `pickedPortfolioId ??
   * calculatedPortfolioId`. */
  pickedPortfolioId: string | null;
};

/**
 * PATCH …/portfolios/:portfolioId — update a goal portfolio. Every field is
 * optional; omitted fields are left unchanged.
 */
export type UpdateGoalPortfolioRequest = {
  name?: string;
  scheduleType?: string;
  oneTimeAmount?: number | null;
  oneTimeDate?: string | null;
  recurringAmount?: number | null;
  recurringFrequency?: string | null;
  recurringStartDate?: string | null;
  recurringEndDate?: string | null;
  portfolioType?: PortfolioType;
  /** QL-72 — pin a model portfolio by hand (an id from the model candidates,
   * the matching one included) or `null` for Automatic (follow the risk score).
   * Needs a `model` portfolio type; a new pick also needs a goal risk score and
   * the collection's `allowModelPortfolioRiskDeviation`. Re-sending the current
   * pick is always accepted. */
  pickedPortfolioId?: string | null;
  lang?: string;
};

/** QL-72 — one model portfolio a goal portfolio may be set to. */
export type ModelPortfolioCandidate = {
  id: string;
  /** Localized to the request language. */
  name: string;
  riskScore: number;
  /** The one the goal's risk score and platform match (what Automatic uses). */
  isMatching: boolean;
};

/**
 * QL-72 — GET …/portfolios/:portfolioId/model-candidates. Active model
 * portfolios of the flow's collection across all risk scores, limited to the
 * goal's platform (untagged ones when the goal has none), sorted by risk score
 * then name.
 */
export type ModelPortfolioCandidatesResponse = {
  candidates: ModelPortfolioCandidate[];
  /** False when the collection setting is off but the portfolio still holds a
   * pick: `candidates` then holds only the current pick, and only Automatic
   * (`pickedPortfolioId: null`) or the current pick is accepted. */
  selectionAllowed: boolean;
  goalRiskScore: number | null;
  /** The portfolio Automatic resolves to (null: nothing matches). */
  calculatedPortfolioId: string | null;
  /** The current hand pick; null = Automatic. */
  pickedPortfolioId: string | null;
};

/** QL-72 — coded errors of a model-portfolio override. */
export type ModelSelectionErrorCode =
  | "model_selection_not_allowed"
  | "model_portfolio_not_eligible";

/**
 * One goal portfolio as served to the flow UI. The stored-row part of the
 * payload is not typed yet, so every other key stays loose.
 */
export type GoalPortfolioResponse = { id: string; goalId: string } & GoalPortfolioComputedFields &
  Record<string, any>;

/**
 * QL-676 — GET /internal/flow-instances/:id/portfolios AND GET /public/flow/portfolios:
 * every portfolio of the instance, grouped by goal in goal order. Each goal's
 * `portfolios` is exactly what GET …/goals/:goalId/portfolios returns, so the
 * instance-wide Portfolio List loads with one request instead of one per goal.
 */
export type FlowInstancePortfoliosResponse = {
  goals: { goalId: string; portfolios: GoalPortfolioResponse[] }[];
};

/**
 * PATCH …/portfolios/:portfolioId/existing-snapshot/:positionId — sell
 * decision for an unmatchable holding (null = keep; a value sells that
 * much, service-capped at the frozen amount). Available on the internal
 * AND public flow surfaces (QL-283 decision 18 — unified action parity).
 */
export type UpdateExistingHoldingRequest = {
  sellAmount: number | null;
};

export type UpdateExistingHoldingResponse = {
  id: string;
  sellAmount: number | null;
  /** Server-side snapshot regeneration result — the sell decision reshapes
   * the proposed composition (QL-304 surviving holdings), so the stored
   * snapshots are rebuilt in the same request like every other
   * composition-shaping mutation. Absent when regeneration failed
   * (best-effort; the next composition edit heals). */
  refreshHints?: SnapshotInvalidation;
};

/**
 * QL-707 — a custom row's suitability as it stood when the advice was submitted,
 * served on LOCKED portfolio reads only (internal + public flow, a loose
 * `customProducts[].suitability`; stripped from every public API response). Lets the
 * read-only builder keep its Suitable column after the product is deleted or
 * deactivated. `null` on the row = could not be evaluated then (shown as
 * unknown); an absent field = a record frozen before this existed (the builder
 * falls back to the live collection).
 */
export type FrozenSuitabilityVerdict = {
  status: SuitabilityStatus;
  isSuitable: boolean;
  issues: SuitabilityIssueType[];
  /** Inputs for the issue messages, frozen with the verdict (names in the flow language). */
  detail: {
    productTypeName?: string;
    /** The product type has an assessment the client did not pass (vs no declared experience). */
    assessmentFailed?: boolean;
    minRiskLevelName?: string;
    minRiskScore?: number;
    minTimeHorizonName?: string;
  };
};

// ─── Switch pairs (QL-283) ───────────────────────────────────────────────────

/**
 * One switch instruction on an account-followup custom portfolio: "move X of
 * the source holding into the target product". The source is an existing
 * snapshot position (matched or unmatchable); the target is a live,
 * amount-based collection product (frozen into the order only at flow
 * completion). Exactly one of switchPercent (amount-mode source, integer
 * 1-100) / switchUnits (units-mode source, whole units) is set.
 */
export type GoalPortfolioSwitchPair = {
  id: string;
  goalPortfolioId: string;
  sourcePositionId: string;
  targetProductId: string;
  /** Live-resolved on editable reads; on locked reads (QL-707) served frozen
   * from the advised record. Frozen into the order at completion. */
  targetProductName: LocalizedString | null;
  targetIsin: string | null;
  switchPercent: number | null;
  switchUnits: number | null;
  order: number;
};

/**
 * Exactly one sizing mode per pair: percent of an amount-mode holding XOR
 * whole units of a units-mode holding. The union makes "both" and "neither"
 * a type error for TS callers; the service still validates at runtime for
 * raw JSON callers.
 */
export type SwitchPairSizing =
  | { switchPercent: number; switchUnits?: never }
  | { switchPercent?: never; switchUnits: number };

/** POST …/portfolios/:portfolioId/switch-pairs (internal + public surfaces). */
export type AddSwitchPairRequest = {
  sourcePositionId: string;
  targetProductId: string;
} & SwitchPairSizing;

/** PATCH …/portfolios/:portfolioId/switch-pairs/:pairId — size change only. */
export type UpdateSwitchPairRequest = SwitchPairSizing;

export type SwitchPairResponse = {
  pair: GoalPortfolioSwitchPair;
  /** Present when the mutation regenerated the stored portfolio snapshots
   * (best-effort — absent means the next composition edit heals). */
  refreshHints?: SnapshotInvalidation;
};

/** DELETE …/switch-pairs/:pairId (internal + public surfaces). */
export type SwitchPairDeleteResponse = {
  success: boolean;
  refreshHints?: SnapshotInvalidation;
};

/** DELETE …/switch-pairs?sourcePositionId= — "clear switch": every pair of
 * one source holding in one call, one snapshot regeneration. */
export type SwitchPairClearResponse = {
  success: boolean;
  deleted: number;
  refreshHints?: SnapshotInvalidation;
};

// ─── Free-scope follow-up selection context (QL-150 phase 2, §9.1/§9.3) ─────
// GET /internal/flow-instances/followup-selection-context — every agreement
// and synced account of the client, grouped by goal family, with per-item
// eligibility (D5). Pending/unsynced accounts AND pending agreements are
// omitted (pending rows are the bank's surface; the tabs hide them too —
// creation is the server backstop). Unknown ACCOUNT holdings never
// block — they freeze as unmatchable keep/sell rows at seeding (QL-203);
// unknown funds block only agreements.

/** One ticked item in a free-scope follow-up selection. */
export type FollowupSelectionItemRef = { type: "account" | "agreement"; id: string };

export type FollowupSelectionRow = {
  type: "account" | "agreement";
  id: string;
  name: string;
  /** The goal the item currently lives on; null for an unlinked item. */
  goalId: string | null;
  goalName: string | null;
  /** Account rows only. */
  wrapperType?: string | null;
  positionCount?: number;
  /**
   * QL-375 group 8 — the active bank mandate naming this account (first by
   * creation when several do). On a flow whose collection has mandates on, a
   * follow-up of the account carries it onto the successor goal.
   */
  mandateId?: string | null;
  mandateName?: string | null;
  /** Agreement rows only. */
  frequency?: string | null;
  /**
   * Per-item verdict (§9.3): an ineligible row renders unselectable with its
   * reason and never blocks the rest of the selection. Agreement reasons:
   * "plan_empty" | "plan_expired" | "plan_duplicate_isin:<ISIN>" (QL-575, a fund in the collection on two lines).
   * Account reasons: "missing_units:<ISIN>". An unmatched agreement line is
   * NOT a block reason — it is reported via droppedIsins below.
   */
  eligible: boolean;
  reason?: string;
  /**
   * Agreement rows only (D5-r, 2026-09-07): ISINs of lines matching no active
   * product. They do NOT block — the line freezes into the baseline but never
   * mirrors into the draft, so completion's replace-all omits it and the fund
   * is dropped at the bank. Render as an explicit per-row warning.
   */
  droppedIsins?: string[];
};

/**
 * Items grouped by CURRENT source goal: one family per goal, plus a singleton
 * family per unlinked item — except that a savings plan and the accounts its
 * lines pay into share one family (QL-567), so an unlinked family can hold
 * several items. Every mode seeds one successor goal per family.
 * Lineage-closure grouping (treating a goal and its follow-up successors as
 * one family, so a later follow-up heals a split) is deferred to QL-369 —
 * `goalIds` stays an array so that switch won't change this contract.
 */
export type FollowupSelectionFamily = {
  /** The family's current goal (exactly one today; several once QL-369 lineage grouping lands); empty for an unlinked family. */
  goalIds: string[];
  /** Family display name (the goal's name); null for an unlinked family. */
  name: string | null;
  items: FollowupSelectionRow[];
};

export type FollowupSelectionContextResponse = {
  /** False only for flow-level blocks; per-item verdicts live on the rows. */
  eligible: boolean;
  /** "not_supported_for_followup" | "no_portfolio_collection" */
  reason?: string;
  /** Selecting more than one item WITHIN one family requires a multi-portfolio flow. */
  multiPortfolio: boolean;
  /** A selection spanning more than one family (one successor goal each)
   * requires a multi-goal flow — the dialog blocks with
   * `flow_not_multi_goal` before anything is created. */
  multiGoal: boolean;
  /** QL-593 — at most this many families (successor goals) in one selection
   * on a multi-goal flow; more is refused with `flow_max_goals_exceeded`. */
  maxGoals: number;
  /** QL-375 group 8 — the flow's collection has mandates on: an account's mandate rides along. */
  mandatesEnabled: boolean;
  families: FollowupSelectionFamily[];
};

/**
 * Follow-up scope mode (locked 2026-09-04): advisors follow up a subset of
 * accounts, a subset of agreements, or the entire client — never an arbitrary
 * account+agreement mix. "client" includes every eligible item (DP-2) with
 * one successor goal per family (DP-1).
 */
export type FollowupMode = "accounts" | "agreements" | "client";

/** POST /internal/flow-instances/followup-selection — mode-scoped creation. */
export type CreateFollowupRequest = {
  flowId: string;
  clientId: string;
  mode: FollowupMode;
  /** Required for "accounts"/"agreements" (the ticked subset, homogeneous); omitted for "client" — the server computes the eligible set. */
  selection?: FollowupSelectionItemRef[];
};

export type CreateFollowupResponse = {
  instanceId: string;
  /** One successor goal per family of the selection context in EVERY mode
   * (see FollowupSelectionFamily). */
  goalIds: string[];
  /** All seeded portfolios across every successor goal, in seeding order. */
  portfolioIds: string[];
};

// ─── Product details modal ──────────────────────────────────────────────────
// GET /internal/flow-instances/products/:productId/details,
// GET /internal/flow-instances/portfolios/:portfolioId/products/:productId/details AND
// GET /public/flow/snapshots/products/:productId/details — same service, same
// shape. Rendered by the portfolio-display product info modal on both surfaces.

export type ProductDetailsResponse = {
  id: string;
  name: string;
  isin: string | null;
  description: string | null;
  productType: { name: string } | null;
  minTimeHorizon: { label: string } | null;
  minRiskLevel: { label: string } | null;
  /** QL-20: `value` is the flow instance's (an advisor override wins); an
   * overridden fee carries the value it replaced in `originalValue`. */
  costs: Array<{ name: string; value: number; originalValue?: number }>;
  assetAllocations: Array<{ name: string; weight: number }>;
  documents: Array<{ name: string; url: string }>;
  /** Preference-ordered localized wrapper type names (first = routing preference); null when the collection has wrapper types disabled. */
  wrapperTypes: string[] | null;
};
